// Address-level geocode triage — judging geocodes BEFORE any voter file exists.
//
// Every geocode surface before this keyed on household_id, so a judgement could not be made until a
// campaign's voter file was loaded, and died with it. That is backwards: whether a pin sits on the
// right roof is a fact about the ADDRESS. This screen keys on addr_key and touches no voter data.
//
// WHAT IT IS FOR, in Caleb's words: "work through an area so I can confidently sell a campaign new
// territory and know my geocodes are solid for their doorknockers." So COMPLETENESS is the
// deliverable, not throughput. The county progress bar is the point of the screen, not decoration —
// it is the thing that answers "is this territory ready to sell".
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createDenseLayer, type DenseData, type DenseLayer } from "./lib/orator-map";
import { api } from "./lib/api";
import { useListQuery, queryData, isFetching } from "./lib/query";
import { loadGoogleMaps } from "./lib/googlemaps";
import { Select } from "./components/ui";

const MAP_ID = (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string) || "DEMO_MAP_ID";

type Addr = {
  addr_key: string; fips: number; county: string | null; full_addr: string | null;
  lat: number | null; lon: number | null; geocode_quality: string | null;
  parcel_verdict: string | null; parcel_addr_count: number | null; parcel_area_m2: number | null;
  county_pct_own: number | null; credibility: string | null;
  conflict_reason: string | null; conflict_detail: string | null;
  conflict_candidates: Cand[] | null;
  // WHAT THIS CARD BECAME. Optional because the queue never needs them: a row in the queue is by
  // definition unjudged, and the moment you judge it the card moves to the next one. They exist
  // for a card you reached by SEARCHING, which deliberately does not move on -- see `judge`.
  review_status?: string | null; placed_just_now?: boolean;
};

// The locations the pass was choosing between when it refused. EVIDENCE, never a recommendation:
// if they could be ranked the pass would not have refused. Shown so a person can look at each, go
// off and check other sources, then take one -- or ignore both and drop their own pin.
type Cand = { lat: number; lon: number; label: string | null; kind: string | null };

type County = {
  fips: number; county: string; suspects: number; placed: number; conflicts: number;
  reviewed: number; remaining: number; pct_done: number | null; pct_own: number | null;
  credibility: string | null;
};

type MapPt = {
  addr_key: string; full_addr: string | null; lat: number; lon: number;
  geocode_quality: string | null; placed_by_human: boolean | null;
  conflict_reason: string | null; review_status: string | null;
  conflict_candidates: Cand[] | null;
  // OPTIONAL BECAUSE THREE ENDPOINTS RETURN THIS SHAPE and only search fills them in. The map and
  // the reviewed list are county-scoped by construction -- they were asked for one county -- so a
  // row from either is in scope and in the county already on screen. Only a search can hand back a
  // row from somewhere else, which is the whole point of 0247.
  fips?: number; county?: string | null;
  post_comm?: string | null; post_code?: string | null;
  in_scope?: boolean;
  record_source?: string | null;
};

// One colour per THING THAT IS TRUE OF THE ADDRESS, so a pattern is visible as a block of colour.
// Seeing a whole street in amber, or a cluster of red on one rooftop, is the point of the map --
// those are the cases where one judgement settles fifty and a card-at-a-time queue hides them.
function ptColour(p: MapPt): string {
  if (p.review_status) return "#5b6472";                       // judged: out of the way, still visible
  if (p.placed_by_human) return "#3ddc84";                     // your pin: permanent
  if (p.conflict_reason) return "#ffb547";                     // machine declined, needs a person
  if (p.geocode_quality === "parcel") return "#4c8dff";        // machine placed it
  return "#ff5252";                                            // still suspect, nothing has touched it
}
// THE SAME PRIORITY AS ptColour, AS A SINGLE CHARACTER. The canvas layer paints by class code, so
// the colouring rule has to exist once and be spelled twice -- here for the layer, above for
// anything reading a single point. They must not drift: a dot that is amber in the legend and blue
// on the map is worse than no colour at all.
function ptClass(p: MapPt): string {
  if (p.review_status) return "j";        // judged
  if (p.placed_by_human) return "y";      // you placed it
  if (p.conflict_reason) return "d";      // the automatic pass declined
  if (p.geocode_quality === "parcel") return "m";   // machine moved it onto the parcel
  return "s";                             // suspect, untouched
}
const PROBLEM_COLOUR: Record<string, string> = {
  j: "#5b6472", y: "#3ddc84", d: "#ffb547", m: "#4c8dff", s: "#ff5252",
};
// Back to front: what still needs a person goes on top of what is already settled.
const PROBLEM_CLASSES = "jmyds";

const LEGEND: [string, string][] = [
  ["#ff5252", "Suspect — untouched"],
  ["#4c8dff", "Moved onto its parcel automatically"],
  ["#ffb547", "Automatic pass declined — needs you"],
  ["#3ddc84", "You placed this pin"],
  ["#5b6472", "Judged"],
];

// 0165 guessed four. Ten minutes of real use killed two of them: nobody is dispatched to confirm a
// geocode, and "I looked and could not settle it" has to be its own revisitable bucket rather than a
// terminal status that hides the row. Placing a pin is the fourth verdict and is always available.
const DISPOSITIONS: { status: string; label: string; key: string; hint: string }[] = [
  { status: "verified",        label: "Looks right",           key: "1",
    hint: "the pin is on the right building" },
  { status: "unresolved",      label: "Can't tell — come back", key: "2",
    hint: "you looked and the evidence does not settle it; kept as its own pile, never confused with unlooked-at" },
  { status: "no_such_address", label: "Address doesn't exist",  key: "3",
    hint: "nothing is here. Reversible — this writes a label, it deletes nothing" },
];

// ── THE ONLY LIST OF KEYS THERE IS ───────────────────────────────────────────────────────────
//
// Caleb, after #750 shipped: "I have never used the quick keys." Five exist. Three are hinted by a
// dim number in front of a button, and two -- Enter and the arrow -- are invisible. He had been
// working counties door by door with the mouse on a screen whose whole deliverable is completeness.
//
// THE VERDICTS ARE READ OUT OF DISPOSITIONS, NOT RETYPED. #751 asked for exactly this, and the
// reason is the failure it prevents: add a fourth verdict to DISPOSITIONS and it appears on the
// card AND in the key handler AND here, in one edit. Retyping the three would let a future verdict
// be pressable and undocumented, which is the state this whole item exists to end.
// `key` is the KeyboardEvent key the handler compares against; `shows` is what gets drawn on the
// kbd chip. They differ for exactly one row -- the handler sees "ArrowRight", a person sees an
// arrow -- and keeping both here is what lets the test read every `e.key === "..."` out of this
// file and assert the guide covers it.
export type Shortcut = { key: string; shows: string; label: string; hint: string };

// THE HANDLER READS THESE, IT DOES NOT RETYPE THEM. The verdicts were already safe -- the buttons
// and the handler both read DISPOSITIONS -- but Enter and the arrow were bare string literals in
// the handler with nothing tying them to anything on screen.
//
// NAMED CONSTS RATHER THAN A .find() LOOKUP, and the difference is the point: `SHORTCUTS.find(...)!`
// types fine even when it returns undefined, so deleting a row would have compiled happily and only
// failed at runtime. Declaring them here and spreading them into the table below means the handler
// references the same object the panel draws, and removing one is a compile error at both sites.
export const SHORTCUT_COMMIT: Shortcut = {
  key: "Enter", shows: "Enter", label: "Place the pin you dragged",
  hint: "only after you have dragged the red pin somewhere. Before that there is nothing to commit, "
      + "and the key does nothing" };
export const SHORTCUT_SKIP: Shortcut = {
  key: "ArrowRight", shows: "\u2192", label: "Skip, and come back later",
  hint: "moves to the next address without recording anything at all about this one" };

export const SHORTCUTS: Shortcut[] = [
  ...DISPOSITIONS.map((d) => ({ key: d.key, shows: d.key, label: d.label, hint: d.hint })),
  SHORTCUT_COMMIT,
  SHORTCUT_SKIP,
];

// A refusal from the placement pass is more informative than silence: the machine got far enough to
// find a contradiction in the county's own file and deliberately stopped short of guessing.
const REVIEW_LABEL: Record<string, string> = {
  verified: "Looks right", unresolved: "Couldn't tell", no_such_address: "Doesn't exist",
};

const CONFLICT_TEXT: Record<string, string> = {
  multiple_streets_one_parcel: "Two parcel records on this spot are claimed by different streets — one is wrong, or the lot was split.",
  multiple_parcels_claim: "Two parcels far apart both claim this address.",
  would_stack_on_another_pin: "Moving it would land on top of a different street's pin.",
  ambiguous_address_key: "Two streets share this name and number — the parcel cannot say which.",
  moves_too_far: "The claiming parcel is far away; likely a same-name street in another town.",
};

type ScopeKind = "county" | "precinct" | "state_house" | "state_senate" | "congressional" | "state_board_ed";
const SCOPE_KINDS: { v: ScopeKind; label: string }[] = [
  { v: "county",         label: "County" },
  { v: "precinct",       label: "Precinct" },
  { v: "state_house",    label: "State house" },
  { v: "state_senate",   label: "State senate" },
  { v: "congressional",  label: "Congressional" },
  { v: "state_board_ed", label: "SBOE" },
];
type Scope = { value: string; label: string; detail: string | null };
type HeldAddr = {
  addr_key: string; fips: number; county: string | null; full_addr: string | null;
  lat: number; lon: number; geocode_quality: string; is_placed: boolean;
  placed_by_human: boolean; authority_lat: number | null; authority_lon: number | null;
  parcel_verdict: string | null; review_status: string | null; placed_just_now?: boolean;
  // ALREADY SENT BY /geocode/address-at SINCE 0232, AND NEVER READ. Caleb, working Travis: "I
  // also don't have like a city state and zip or any other corroborating information I just have
  // the street address showing". The server was sending both fields the whole time; this type
  // dropped them on the floor, so the strip under the map could not show what it had.
  post_comm?: string | null; post_code?: string | null;
  record_source?: string | null;
};

// ── WHERE AN ADDRESS RECORD CAME FROM ────────────────────────────────────────────────────────
//
// 0248 printed "no town or ZIP" here and I told Caleb it meant the row came from an appraisal roll
// rather than the county's 9-1-1 file. IT DOES NOT, and the numbers were on file before it shipped:
// 2,684,704 of the 12,140,047 rows FROM the 9-1-1 file carry no post_code either -- including every
// one of Bexar's 721,120, whose file has none at all. Work Bexar and that line appears on every pin
// on the screen and separates nothing.
//
// An absence is evidence of an absence. The provenance is stored outright, on address_point, exact
// across all 13,576,148 rows, and it is what the strip says now.
//
// NOT `geocode_source` FROM THE VIEW, which is overridden by an active placement and answers "who
// put this pin here". `record_source` is the address row's own, carried under its own name so the
// two questions cannot be read off one word.
const RECORD_SOURCE: Record<string, { label: string; hint: string }> = {
  parcel: {
    label: "from the appraisal roll",
    hint: "Not in the county's 9-1-1 file. This address exists because an appraisal district's "
        + "parcel record named it, and those are reliable about the LOT and often wrong about the "
        + "address — where both name the same door, the roll's ZIP disagrees with the 9-1-1 file "
        + "1.4% to 8.7% of the time depending on the county.",
  },
  human: {
    label: "added by hand",
    hint: "Somebody stood in front of this door and added it because the state file did not have "
        + "it. Its pin is permanent — no automated pass can move it.",
  },
  // 'txgio' is the county's own 9-1-1 file and is 89% of the vault. Badging the normal case would
  // make the badge furniture, and a badge everybody stops reading cannot flag the 11%.
};

// ── WHAT GOOGLE ACTUALLY HANDS BACK ──────────────────────────────────────────────────────────
//
// Google's geocoder DOES NOT SAY "NOT FOUND". Measured against the production key 2026-09-11:
//
//   "99999 ZZQQXX NONEXISTENT BLVD"  -> 2 results, both "The Blvd, Texas, USA", GEOMETRIC_CENTER,
//                                       partial_match
//   "14722 NOWHERE AT ALL RD"        -> 1 result,  "Texas, USA", APPROXIMATE, partial_match
//   "ZZZZZZZZ"                       -> 1 result,  "Texas, USA", APPROXIMATE, partial_match
//
// So a screen that renders "the first result" as an answer will happily offer to move a pin to the
// centroid of Texas, and it will look exactly like a real answer. location_type is the field that
// separates them and it is not optional reading:
//
//   ROOFTOP             a house. Google has this address.
//   RANGE_INTERPOLATED  a house number estimated between two known ones on that block.
//   GEOMETRIC_CENTER    the middle of a road. Google found the STREET, not the address.
//   APPROXIMATE         a town, a postcode, or the state. Not an address at all.
//
// Only the first two may move a pin. The rest are shown -- suppressing them would look like "no
// answer", and "Google found the road but not the house" is a different and useful fact -- but
// they carry no button, because there is nothing there to move a pin to.
type GeoHit = {
  label: string; lat: number; lon: number;
  precision: string; partial: boolean; m: number | null; dir: string | null;
};
// METRES CLOSE IN, MILES FAR OUT. The rest of this screen is metric because everything it measures
// is a rooftop's neighbourhood -- 60 m, 150 m, lot sizes in m². A lookup answer is routinely a
// hundred miles away, and "138 km" is not a distance anybody here has an instinct for.
function geoDistance(m: number | null, dir: string | null): string | null {
  if (m == null) return null;
  const d = dir ? ` ${dir}` : "";
  return m < 1000 ? `${Math.round(m)} m${d} away`
                  : `${(m / 1609.344).toFixed(m < 16093 ? 1 : 0)} miles${d}`;
}
// EXPORTED SO A TEST CAN REACH IT, for the same reason parcelDataLabel below is. This one
// decides whether a Google answer is allowed to move a pin, and getting it wrong means the
// screen offers to move a door to the centroid of Texas -- which is a failure nobody looking
// at the page would recognise as one.
export function geoIsPlaceable(precision: string): boolean {
  return precision === "ROOFTOP" || precision === "RANGE_INTERPOLATED";
}
const GEO_PRECISION: Record<string, string> = {
  ROOFTOP: "a house Google knows",
  RANGE_INTERPOLATED: "estimated between the house numbers either side",
  GEOMETRIC_CENTER: "the middle of that road — Google found the street, not the house",
  APPROXIMATE: "a town or a postcode — not an address",
};
type UndoEntry = { placementId: number; index: number; label: string;
                   from: { lat: number; lon: number } };

/** What we can honestly say about this county's parcel data, in one clause. Never empty.
 *
 * EXPORTED SO A TEST CAN REACH IT. The bug this replaces was an ABSENCE -- the clause was gated on
 * `pct_own != null`, so the counties we had not measured rendered nothing at all, and a missing
 * sentence is indistinguishable from a sentence nobody wrote. An absence cannot be asserted from
 * inside a 1,600-line screen; a function returning a string can.
 *
 * THREE STATES, because county_credibility() reports three and the screen was collapsing two of
 * them into silence:
 *   - a grade and a percentage  -> we measured it, and here is how well it went
 *   - 'unmeasured' (pct_own null) -> we have no measurement. Note this is NOT the same claim as
 *     "this county has no parcel file": a county under the 1,000-point credibility floor lands
 *     here too, and saying "none" would be inventing which of the two it is.
 *   - no row at all -> we do not know, and saying so is the honest answer.
 */
// ── THE RESULTS LIST AFTER YOU ACT ON A ROW ──────────────────────────────────────────────────
//
// MARK THE ROW, NEVER REMOVE IT. This is the whole of the transition bug. The first version
// FILTERED the row you judged out of the results, so when it was the only in-county match,
// SearchHits fell through to its next case and printed
//
//     "Nothing in TRAVIS -- but this address exists elsewhere in Texas."
//
// directly above the card. That sentence is a statement about the SEARCH, and it was being
// produced seconds later by the person's own verdict on the row the search had just found.
//
// The correctness test is simple: after acting on a row, the list must read exactly as a fresh
// search of the same text returns it -- greyed dot, "judged" tag, still clickable. Marking gives
// that for free; filtering cannot.
//
// Exported so the behaviour is testable without standing up the whole screen.
export function hitsAfterVerdict(hits: MapPt[] | null, addr_key: string, fips: number,
                                 verdict: string | null): MapPt[] | null {
  // A row from the map or the reviewed list carries no fips -- it was asked for one county, so it
  // is in the county on screen by construction. Only a search can return another county's row, and
  // those carry theirs. Hence: match on fips only when the row has one to match.
  return hits && hits.map((x) =>
    x.addr_key === addr_key && (x.fips == null || x.fips === fips)
      ? { ...x, review_status: verdict } : x);
}

export function hitsAfterPlacement(hits: MapPt[] | null, addr_key: string, fips: number,
                                   lat: number, lon: number): MapPt[] | null {
  return hits && hits.map((x) =>
    x.addr_key === addr_key && (x.fips == null || x.fips === fips)
      ? { ...x, placed_by_human: true, lat, lon } : x);
}

export function parcelDataLabel(credibility: string | null, pctOwn: number | null): string {
  if (credibility == null) return "parcel data: unknown for this county";
  if (pctOwn == null) return "parcel data: not measured here — these pins have not been checked against a lot";
  return `parcel data: yes — ${credibility} (${pctOwn}% own-parcel)`;
}

export function Addresses({ initialFips }: { initialFips?: number } = {}) {
  // OPENED ON A COUNTY WHEN SOMETHING SENT YOU HERE. The vault overview ranks the counties holding
  // the conflicted pins; a link that lands on an unfiltered statewide queue makes the reader find
  // their county again in a 254-entry select, which is the same as not linking at all.
  const [fips, setFips] = useState<number | "">(initialFips ?? "");
  const [order, setOrder] = useState<"worst_first" | "grouped">("worst_first");
  const [i, setI] = useState(0);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);              // judged in this sitting
  const [view, setView] = useState<"map" | "queue" | "reviewed">("map");
  const [revStatus, setRevStatus] = useState("");   // "" = every verdict
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<MapPt[] | null>(null);
  const [picked, setPicked] = useState<Addr | null>(null);    // chosen from the map or from search
  // ADDING A DOOR THE STATE FILE DOES NOT HAVE. Every other control on this screen corrects an
  // address TxGIO published; this is the one that answers "I am standing in front of a house and it
  // is not on the map". Null except while that is being done, so the map behaves normally the rest
  // of the time.
  // CITY AND ZIP ARE PART OF THE RECORD, NOT EXTRAS. They are what tells two addresses on the same
  // household key apart -- 146 HONEYSUCKLE ROAD and 146 HONEYSUCKLE LANE share one key and sit 50 km
  // apart -- and post_comm is what the interpolation pass scopes a street by. An address added
  // without them is one nobody can later distinguish from its twin, so the form asks and says why.
  const [creating, setCreating] = useState<
    { full_addr: string; unit: string; city: string; zip: string; lat: number | null; lon: number | null } | null>(null);
  // WHAT IS ALREADY AT THIS SPOT. The duplicate this feature can create is the near miss: you type
  // "404 Found Ln", the county calls it "404 N Found Ln", the two key differently and you get two
  // rows on one roof -- the same shape as the 5,742 retired on 2026-09-09. Nothing reconciles that
  // afterwards, so it is caught at entry: show what is within reach of the pin before saving.
  const [nearby, setNearby] = useState<
    { id: number | null; full_addr: string; addr_key: string; m: number;
      post_comm: string | null; post_code: string | null;
      quality: string | null; conflict: string | null }[] | null>(null);
  // WHICH DOORS THE MAP DRAWS, and it is the difference between a queue and an audit.
  //
  // "problems" is the original view: /geocode/address-map, capped at 4,000, filtered to suspect,
  // parcel and conflicts. It answers "what is left to work".
  //
  // "every" answers the question that had no screen at all -- IS OUR WORK RIGHT? A queue of flagged
  // addresses cannot show you the errors it failed to flag. Seeing every door, coloured by who put
  // the pin there, is how a whole street offset the same direction or a subdivision we are
  // confidently wrong about becomes visible. It rides /geocode/address-canvas at 120,000 a time and
  // reloads on the viewport, because a county is 721k rows and ten seconds.
  const [density, setDensity] = useState<"problems" | "every">("problems");
  // WHAT THE "EVERY DOOR" MAP IS POINTED AT. 0232 shipped with "pick a county" and nothing else,
  // which is not how anybody works: turf is licensed by district, cut by precinct and walked by
  // block. It was also the slower choice -- HD 121 is 67,693 doors in 360 ms, the whole district
  // uncapped, against 749 ms for a 120,000-door slice of Bexar and 9.7 s to count the county.
  //
  // A district or precinct is bounded by its own definition, so it loads once, complete, and the
  // map is free to pan and zoom over it without refetching. A county is not bounded by anything
  // usable, so it stays viewport-driven and truncates honestly.
  const [scopeKind, setScopeKind] = useState<ScopeKind>("county");
  const [scopeValue, setScopeValue] = useState<string>("");
  const [dense, setDense] = useState<{ n: number; truncated: boolean } | null>(null);
  const [denseBusy, setDenseBusy] = useState(false);
  // The door currently held or last touched, and the stack of moves that can be taken back. The
  // stack is per-sitting and deliberately not persisted: an undo that survives a reload would let
  // somebody reverse a placement they no longer remember making.
  const [heldAddr, setHeldAddr] = useState<HeldAddr | null>(null);
  const [undo, setUndo] = useState<UndoEntry[]>([]);
  // WHICH DOT ON THE CANVAS THE HELD ADDRESS IS. Needed only so that accepting a lookup result can
  // move the dot the same way a drag does; without it the database would hold the new coordinate
  // and the map would still be drawing the old one, which is the state the drag handler's own
  // error path exists to avoid.
  const [heldIdx, setHeldIdx] = useState<number | null>(null);
  // ONE LOOKUP AT A TIME, TAGGED WITH THE ADDRESS IT BELONGS TO. Clicking a different pin must not
  // leave the previous address's Google results sitting under the new one, which is how somebody
  // moves the wrong door to the right place.
  const [lookup, setLookup] = useState<
    { key: string; q: string; hits: GeoHit[] | null; err: string | null; running: boolean } | null>(null);

  const scope = fips === "" ? "" : String(fips);

  const { query, reload, patch } = useListQuery<{ addresses: Addr[] }>(
    `addr:${scope}:${order}`,
    (signal) => api(`/geocode/addresses?order=${order}&limit=200${scope ? `&fips=${scope}` : ""}`, { signal }),
    { enabled: scope !== "", resetKey: `${scope}:${order}` },
  );

  const counties = useListQuery<{ counties: County[] }>(
    "addr-counties",
    (signal) => api(`/geocode/address-counties`, { signal }),
  );
  const countyList = queryData(counties.query)?.counties ?? [];
  const chosen = countyList.find((c) => c.fips === fips);

  // WHAT HAS ALREADY BEEN JUDGED. A one-way queue is not trustworthy -- no way to undo a bad call,
  // no way to see your own consistency, and nowhere for the "couldn't tell" pile to live until there
  // is better evidence.
  const rev = useListQuery<{ reviewed: (MapPt & { status: string; note: string | null;
                                                  reviewed_at: string | null })[];
                             counts: Record<string, number> }>(
    `addrrev:${scope}:${revStatus}`,
    (signal) => api(`/geocode/address-reviewed?fips=${scope}${revStatus ? `&status=${revStatus}` : ""}`,
                    { signal }),
    { enabled: scope !== "" && view === "reviewed", resetKey: `${scope}:${revStatus}` },
  );

  const mapQ = useListQuery<{ points: MapPt[]; truncated: boolean; cap: number }>(
    `addrmap:${scope}`,
    (signal) => api(`/geocode/address-map?fips=${scope}`, { signal }),
    { enabled: scope !== "" && view === "map" && density === "problems", resetKey: scope },
  );

  const rows = queryData(query)?.addresses ?? [];
  const cur = picked ?? rows[i];


  // ── map ──────────────────────────────────────────────────────────────────────────────────────
  // A CALLBACK REF, NOT useRef + [] .
  //
  // The first version created the map in a useEffect with an empty dependency array. That runs ONCE,
  // at mount -- when no county is chosen and the map container is not rendered at all. elRef.current
  // was null, the effect returned early, and nothing ever ran again: choosing a county produced the
  // container and no map. A blank box, no error, nothing in the console.
  //
  // A callback ref fires when the element ATTACHES, which is the actual event we care about, so
  // there is no dependency to predict wrongly.
  const [mapEl, setMapEl] = useState<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const googleRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const candRef = useRef<any[]>([]);
  const resizeObsRef = useRef<ResizeObserver | null>(null);
  const [dragged, setDragged] = useState<{ lat: number; lon: number } | null>(null);
  // Bumped once the map exists. The overview and judging layers both read googleRef/mapRef, which are
  // refs -- changing a ref does not re-render, so without this they would run before the map is built
  // and silently do nothing.
  const [mapReady, setMapReady] = useState(0);

  useEffect(() => {
    if (!mapEl || mapRef.current) return;
    let cancelled = false;
    // NEVER BUILD A MAP IN A ZERO-HEIGHT BOX -- it initialises at 0x0 and stays broken even after the
    // container grows. If the element is not laid out yet, wait a frame and look again.
    const build = () => {
      if (cancelled || mapRef.current) return;
      // BOTH DIMENSIONS. Gating on height alone let the map build before the flex row had resolved a
      // width: Maps fell back to its default 1024x768 renderer, drew nothing, and never recovered --
      // a blank grey box with no console error. It only came back when the container CHANGED size,
      // which is why switching to the queue view appeared to "fix" it.
      if (mapEl.offsetWidth < 200 || mapEl.offsetHeight < 200) { requestAnimationFrame(build); return; }
      loadGoogleMaps().then((google: any) => {
        if (cancelled || mapRef.current) return;
        googleRef.current = google;
        mapRef.current = new google.maps.Map(mapEl, {
          center: { lat: 31.0, lng: -99.0 }, zoom: 6, mapId: MAP_ID, mapTypeId: "hybrid",
          mapTypeControl: true, streetViewControl: true, fullscreenControl: false,
          gestureHandling: "greedy",
        });
        setMapReady((n) => n + 1);      // wakes the layers, which cannot run before a map exists

        // AND KEEP TELLING IT. The container changes size for real in normal use -- the judging card
        // appears beside the map and takes 340px with it. A map that measured once at creation is
        // one layout change away from the same blank box, so nudge it whenever the box moves.
        const ro = new ResizeObserver(() => {
          const m = mapRef.current;
          if (!m) return;
          const c = m.getCenter();
          google.maps.event.trigger(m, "resize");
          if (c) m.setCenter(c);        // vector maps ignore 'resize'; re-centring forces the redraw
        });
        ro.observe(mapEl);
        resizeObsRef.current = ro;
      }).catch((e: any) => setMsg(String(e?.message || e)));
    };
    build();
    return () => { cancelled = true; resizeObsRef.current?.disconnect(); resizeObsRef.current = null; };
  }, [mapEl]);

  // Recentre on the current address and drop a DRAGGABLE pin. Dragging is the correction path, so it
  // is always available rather than hidden behind a mode — most addresses need a verdict, some need
  // a nudge, and switching modes for the ones that need a nudge is friction on every single card.
  useEffect(() => {
    const google = googleRef.current, map = mapRef.current;
    // BOTH VIEWS DRIVE ONE MAP, so each must stay off it while the other owns it. Without this the
    // judging effect recentres on the current card while the overview is trying to fit the county,
    // and the map jitters between the two.
    if (view !== "queue") { if (markerRef.current) { markerRef.current.map = null; markerRef.current = null; } return; }
    if (!google || !map || !cur || cur.lat == null || cur.lon == null) return;
    setDragged(null);
    const pos = { lat: cur.lat, lng: cur.lon };
    map.setCenter(pos);
    map.setZoom(20);                       // rooftop zoom: the judgement is "is this the right roof"
    if (markerRef.current) markerRef.current.map = null;
    // A plain DOM element, the same shape Geocode.tsx uses. PinElement would be tidier but nothing
    // else in this codebase exercises it, and an untested marker API is not worth the risk on a
    // screen whose whole job is showing a pin in the right place.
    const dot = document.createElement("div");
    dot.style.cssText = "width:18px;height:18px;border-radius:50%;background:#ff5252;"
      + "border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.5);cursor:grab";
    const m = new google.maps.marker.AdvancedMarkerElement({
      position: pos, map, content: dot, gmpDraggable: true,
      title: cur.full_addr || cur.addr_key,
    });
    m.addListener("dragend", (ev: any) => {
      setDragged({ lat: ev.latLng.lat(), lon: ev.latLng.lng() });
    });
    markerRef.current = m;

    // CANDIDATES, lettered A/B/C, click to take. The current pin stays put so the choice is always
    // "here, or one of these" -- and dragging remains available, because the right answer is often
    // neither and a person with Street View open will know that.
    candRef.current.forEach((x) => { x.map = null; });
    candRef.current = [];
    const cands = cur.conflict_candidates || [];
    if (cands.length) {
      const b = new google.maps.LatLngBounds();
      b.extend(pos);
      cands.forEach((c, n) => {
        const el = document.createElement("div");
        el.style.cssText = "width:26px;height:26px;border-radius:50%;background:#ffb547;color:#1a1d23;"
          + "font:700 13px system-ui;display:flex;align-items:center;justify-content:center;"
          + "border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.5);cursor:pointer";
        el.textContent = String.fromCharCode(65 + n);
        const cm = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: c.lat, lng: c.lon }, map, content: el,
          title: c.label || "Candidate location",
        });
        // 'gmp-click', not 'click': AdvancedMarkerElement warns that plain click is deprecated and
        // will stop firing. The console said so; worth heeding before it silently breaks choosing.
        cm.addListener("gmp-click", () => setDragged({ lat: c.lat, lon: c.lon }));
        candRef.current.push(cm);
        b.extend({ lat: c.lat, lng: c.lon });
      });
      // Fit the pin AND the candidates: the whole question is where they are relative to each other.
      map.fitBounds(b, 80);
    }
  }, [cur?.addr_key, view, mapReady]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── WHAT IS LEFT TO WORK: THE SAME LAYER "EVERY DOOR" USES, SO IT DRAGS THE SAME WAY ────────
  //
  // This was its own bespoke canvas overlay, and clicking a dot navigated into the judging queue.
  // Caleb, working Aransas: "every time I try to do that here and it sticks me back in the queue
  // it's a real problem". He is right, and the comment on the drag handler below already said so
  // about the OTHER layer -- clicking to navigate "costs a screen change per door", which is the
  // opposite of the point. The fix had been written once and wired to one of the two layers.
  //
  // So this uses createDenseLayer, exactly as "Every door" does. One implementation, one hit test,
  // one drag gesture, two datasets. What differs is only WHICH points are loaded and how they are
  // coloured, which is what the options are for.
  //
  // The old overlay's reason for existing still holds and is why the shared layer is built the same
  // way: 4,000 AdvancedMarkerElements meant 4,000 DOM nodes, and tearing them down froze the
  // renderer hard enough that Chrome offered to kill the page. One canvas, one node, hit-testing by
  // a loop over an array we already have.
  // ONE LAYER REF FOR BOTH DENSITIES. Declared here because the "what is left to work" effect below
  // is the first to build one; "every door" further down reuses the same ref, and they are mutually
  // exclusive on `density` so they can never both own it.
  const denseRef = useRef<DenseLayer | null>(null);
  const denseIdsRef = useRef<number[]>([]);
  // THE COORDINATES THE CANVAS IS ALREADY HOLDING. Finding what is near a point is a question the
  // browser can answer for free: the layer has every loaded door's lat/lon in memory. Asking the
  // database instead costs 11.4 SECONDS on Bexar -- measured, not guessed -- because address_point
  // has no spatial index and a county scan filters 372,084 rows to find two.
  const denseLatRef = useRef<number[]>([]);
  const denseLonRef = useRef<number[]>([]);
  const ptsRef = useRef<MapPt[]>([]);
  const pts = useMemo(() => queryData(mapQ.query)?.points ?? [], [mapQ.query]);
  ptsRef.current = pts;
  // Read inside the map's mousedown handler, which is registered once per view/density. Using the
  // state there would capture the value from the render that installed the listener.
  const creatingRef = useRef<typeof creating>(null);
  creatingRef.current = creating;
  const createMarkerRef = useRef<any>(null);
  // The map's mousedown is registered once per view/density; findNearby closes over state that
  // changes every render, so it is reached through a ref rather than re-subscribing the listener.
  const findNearbyRef = useRef<((lat: number, lon: number) => void) | null>(null);

  useEffect(() => {
    const google = googleRef.current, map = mapRef.current;
    if (!google || !map) return;
    if (view !== "map" || density === "every" || pts.length === 0) {
      if (density !== "every") { denseRef.current?.destroy(); denseRef.current = null; }
      return;
    }
    denseRef.current?.destroy();
    denseRef.current = createDenseLayer(google, map, {
      colorFor: (c) => PROBLEM_COLOUR[c] || "#888",
      classes: PROBLEM_CLASSES,
      // Bigger than the 120,000-door layer on purpose: there are a few thousand here, they are the
      // ones being worked, and a 2 px dot is not a drag target.
      radius: 5,
    });
    // id is the INDEX here, not a database id. These points arrive with their addr_key already --
    // the dense layer has to fetch it, this one does not -- so the drag handler reads straight off
    // ptsRef rather than asking the server what it is already holding.
    denseRef.current.setData({
      id: pts.map((_, i) => i),
      lat: pts.map((p) => p.lat),
      lon: pts.map((p) => p.lon),
      st: pts.map(ptClass).join(""),
    });
    return () => { denseRef.current?.destroy(); denseRef.current = null; };
  }, [pts, view, density, mapReady]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── EVERY DOOR: 120,000 AT A TIME, DRIVEN BY THE VIEWPORT ────────────────────────────────────
  //
  // Six colours, and the split is "who put this pin here" rather than "how good is it" -- an auditor
  // is looking for OUR mistakes, so our three kinds of edit are separated from the county's three
  // kinds of label. Painted back to front: the mass of untouched doors first, our edits over them,
  // disputed pins last so they are never hidden under anything.
  // TUNED FOR SATELLITE, NOT FOR A WHITE PAGE. The first cut used a muted dark green for the
  // background class and it disappeared entirely over tree canopy -- which is most of East Texas.
  // Every colour here is high-chroma and light enough to sit on dark imagery, and every dot is
  // drawn with a dark outline (below) so it survives a light roof too. There is no "quiet" colour
  // on a satellite basemap; there is only visible and invisible.
  const DENSE_COLOUR: Record<string, string> = {
    r: "#7fe3a4",   // untouched, county calls it a rooftop
    l: "#ffd24a",   // untouched, county calls it a lot or centroid
    p: "#5ab8ff",   // we placed it on a parcel centroid
    b: "#22e06a",   // we snapped it onto a building
    h: "#e08bff",   // a person placed it
    s: "#ff5545",   // untouched and the parcel check disputes it
  };
  const DENSE_LEGEND: [string, string][] = [
    ["b", "we snapped it to a building"], ["p", "we placed it on the lot"],
    ["h", "a person placed it"], ["r", "county says rooftop"],
    ["l", "county says lot"], ["s", "disputed"],
  ];
  // The pickable values for the chosen kind. Precincts need a county to narrow them -- there are
  // ~9,600 statewide and a list of all of them is not a picker.
  const scopesQ = useListQuery<{ scopes: Scope[] }>(
    `vaultscopes:${scopeKind}:${scopeKind === "precinct" ? scope : ""}`,
    (signal) => api(`/geocode/vault-scopes?kind=${scopeKind}`
                    + (scopeKind === "precinct" ? `&fips=${scope}` : ""), { signal }),
    { enabled: density === "every" && scopeKind !== "county"
               && (scopeKind !== "precinct" || scope !== ""),
      resetKey: `${scopeKind}:${scope}` },
  );
  const scopes = queryData(scopesQ.query)?.scopes ?? [];
  // A value from the previous kind is meaningless against the new one -- "121" is a house district
  // and also somebody's precinct. Clear it rather than silently querying the wrong scope.
  useEffect(() => { setScopeValue(""); }, [scopeKind, scope]);

  // ── EVERYTHING ELSE A COUNTY CHANGE INVALIDATES ──────────────────────────────────────────────
  //
  // AN EFFECT, NOT A LINE IN EACH HANDLER, and that is the whole lesson of 0247-0249: three
  // consecutive fixes for three call sites that each read "the county you are working" as a
  // constant. There are now TWO ways it changes -- the select, and clicking a search result from
  // another county -- and a third will be added by somebody who has not read any of this. The
  // effect above has had the right shape since 0233; it just only covered one field.
  //
  // WHAT BREAKS: anything holding an INDEX INTO THE LOADED LAYER. The canvas is rebuilt per county,
  // so index 1,234 is a different door afterwards.
  //
  //   `undo`    carries one. undoLast withdraws the RIGHT placement -- it is keyed by placement_id
  //             -- and then calls moveTo(top.index, ...) against the new county's layer, dragging an
  //             unrelated pin to the old address's coordinate. The database stays correct and the
  //             map lies, which is the worse half. The button even says so out loud: "Undo — 21461
  //             COYOTE TRL" while you are looking at Hays.
  //   `heldIdx` is the same index for the same reason, read by placeHeld.
  //
  // ...and anything BOUND TO ONE COUNTY: a half-typed new address belongs to the county it was
  // started in (create_address writes into one), and `nearby` is a list of that county's doors.
  //
  // NOT `picked` AND NOT `hits`. Those are what a click on an out-of-county result is MADE of --
  // clearing them here would undo the navigation that just set the county, which is the bug this
  // file spent three deploys fixing.
  useEffect(() => {
    setUndo([]); setHeldAddr(null); setHeldIdx(null); setCreating(null); setNearby(null);
  }, [scope]);

  useEffect(() => {
    const google = googleRef.current, map = mapRef.current;
    if (!google || !map) return;
    const county = scopeKind === "county";
    // A county is identified by the county picker; everything else by its own value.
    const ready = county ? scope !== "" : scopeValue !== "";
    if (view !== "map" || density !== "every" || !ready) {
      denseRef.current?.destroy(); denseRef.current = null;
      denseIdsRef.current = []; denseLatRef.current = []; denseLonRef.current = [];
      setDense(null);
      return;
    }
    if (!denseRef.current) {
      denseRef.current = createDenseLayer(google, map, { colorFor: (c) => DENSE_COLOUR[c] || "#888" });
    }
    const layer = denseRef.current;
    let cancelled = false;
    let timer: any = null;

    // ONE FETCH PER SETTLED VIEWPORT, NOT PER FRAME. `idle` already fires after panning stops, but a
    // zoom emits several in quick succession; 250 ms of debounce turns a pinch-zoom into one request
    // instead of five, each of which is 120,000 rows.
    async function load() {
      let box = "";
      if (county) {
        const b = map.getBounds(); if (!b) return;
        const ne = b.getNorthEast(), sw = b.getSouthWest();
        box = `&min_lat=${sw.lat()}&min_lon=${sw.lng()}&max_lat=${ne.lat()}&max_lon=${ne.lng()}`;
      }
      setDenseBusy(true);
      try {
        const r = await api<DenseData & { n: number; truncated: boolean }>(
          `/geocode/address-canvas?kind=${scopeKind}`
          + `&value=${encodeURIComponent(county ? String(scope) : scopeValue)}${box}`);
        if (cancelled) return;
        denseIdsRef.current = r.id;
        denseLatRef.current = r.lat;
        denseLonRef.current = r.lon;
        layer.setData(r);
        setDense({ n: r.n, truncated: r.truncated });
        // FRAME THE SCOPE, ONCE. A district loads complete and the map is still sitting on
        // whatever county the queue last looked at, so the doors would be off-screen entirely.
        // Only on the first load of a scope -- refitting on every fetch would fight the user's pan.
        if (!county && r.lat.length) {
          const b2 = new google.maps.LatLngBounds();
          for (let i = 0; i < r.lat.length; i += Math.max(1, Math.floor(r.lat.length / 2000)))
            b2.extend({ lat: r.lat[i], lng: r.lon[i] });
          if (!b2.isEmpty()) map.fitBounds(b2, 40);
        }
      } catch (e: any) {
        if (!cancelled) setMsg(String(e?.message || e));
      } finally { if (!cancelled) setDenseBusy(false); }
    }
    // ONLY A COUNTY REFETCHES ON PAN. A district or precinct came back whole, so panning and
    // zooming over it is free -- re-requesting 67,000 doors because the viewport moved four pixels
    // is exactly the waste the scope was introduced to avoid.
    const onIdle = county
      ? map.addListener("idle", () => { clearTimeout(timer); timer = setTimeout(load, 250); })
      : null;
    load();
    return () => {
      cancelled = true; clearTimeout(timer);
      if (onIdle) google.maps.event.removeListener(onIdle);
      denseRef.current?.destroy(); denseRef.current = null;
    };
  }, [view, density, scope, scopeKind, scopeValue, mapReady]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── PICK A DOT UP, PUT IT WHERE IT BELONGS ───────────────────────────────────────────────────
  //
  // The point of the big map is speed: read the address off the dot, look at the roof on the
  // satellite, drag the dot onto it, move on. Clicking used to navigate into the judging queue,
  // which is the opposite -- it costs a screen change per door and the queue only holds the ones
  // the parcel check already complained about. Here, clicking a dot tells you what it is, and
  // dragging it moves the pin for real.
  //
  // WHY MOUSEDOWN AND NOT google.maps DRAG. The canvas is one DOM node with pointer-events:none;
  // there is nothing per-dot to attach a drag to, which is exactly what makes 120,000 affordable.
  // So the map's own mouse events drive it: mousedown hit-tests, and while a pin is held the map
  // stops panning so the gesture cannot do both at once.
  useEffect(() => {
    const google = googleRef.current, map = mapRef.current;
    if (!google || !map || view !== "map") return;

    let holding: number | null = null;
    let moved = false;

    // WHERE THE ADDRESS COMES FROM DIFFERS BY LAYER, AND ONLY THAT. "Every door" is fed by
    // /geocode/address-canvas, which returns ids and coordinates and nothing else, so reading a dot
    // costs a round trip. "What is left to work" already arrived with addr_key, fips and full_addr
    // on every point -- asking the server to tell us what we are holding would be a request whose
    // answer is in memory.
    function ptAt(i: number): HeldAddr | null {
      const p = ptsRef.current[i];
      if (!p) return null;
      return { addr_key: p.addr_key, fips: Number(scope), county: null, full_addr: p.full_addr,
               lat: p.lat, lon: p.lon, geocode_quality: p.geocode_quality || "",
               is_placed: !!p.placed_by_human || p.geocode_quality === "parcel",
               placed_by_human: !!p.placed_by_human, authority_lat: null, authority_lon: null,
               parcel_verdict: null, review_status: p.review_status,
               // The "what is left to work" layer does not carry them and inventing a blank is not
               // the same claim as "the county left this empty". Undefined means unknown here.
               post_comm: undefined, post_code: undefined,
               record_source: p.record_source };
    }

    async function describe(i: number) {
      setHeldIdx(i);
      if (density !== "every") { setHeldAddr(ptAt(i)); return; }
      const id = denseIdsRef.current[i]; if (id == null) return;
      try { setHeldAddr(await api<HeldAddr>(`/geocode/address-at?id=${id}`)); }
      catch (e: any) { setMsg(String(e?.message || e)); }
    }

    const down = map.addListener("mousedown", (e: any) => {
      // WHILE ADDING A DOOR, THE MAP IS FOR CHOOSING A SPOT -- and it must not also hit-test the
      // dots. Sharing one gesture between "put the new house here" and "pick up that pin" is how a
      // click meant to place a new address silently moves a neighbour's instead.
      if (creatingRef.current) {
        if (!e.latLng) return;
        const lat = e.latLng.lat(), lon = e.latLng.lng();
        setCreating((c) => (c ? { ...c, lat, lon } : c));
        findNearbyRef.current?.(lat, lon);
        return;
      }
      const layer = denseRef.current; if (!layer || !e.latLng) return;
      const i = layer.hit(e.latLng, 10);
      if (i == null) return;
      holding = i; moved = false;
      layer.setHeld(i);
      // The map must not pan while a pin is held, or one gesture does two things and neither
      // lands where it looked like it would.
      map.setOptions({ draggable: false });
      setHeldAddr(null);
      describe(i);
    });

    const move = map.addListener("mousemove", (e: any) => {
      if (holding == null || !e.latLng) return;
      moved = true;
      denseRef.current?.moveTo(holding, e.latLng.lat(), e.latLng.lng());
    });

    const up = map.addListener("mouseup", async (e: any) => {
      if (holding == null) return;
      const i = holding, layer = denseRef.current;
      holding = null;
      map.setOptions({ draggable: true });
      layer?.setHeld(null);
      // A CLICK IS NOT A DRAG. Without this, every click that reads an address would also write a
      // placement putting the pin exactly where it already was -- thousands of no-op human
      // placements, each of which is sticky against every future automated pass.
      if (!moved || !e.latLng) return;
      try {
        const a = density === "every"
          ? await api<HeldAddr>(`/geocode/address-at?id=${denseIdsRef.current[i]}`)
          : ptAt(i);
        if (!a) return;
        const r = await api<{ placement_id: number }>("/geocode/address-place", {
          method: "POST",
          body: JSON.stringify({ fips: a.fips, addr_key: a.addr_key,
                                 lat: e.latLng.lat(), lon: e.latLng.lng(),
                                 note: "moved on the county map" }),
        });
        setUndo((u) => [{ placementId: r.placement_id, index: i, label: a.full_addr || a.addr_key,
                          from: { lat: a.lat, lon: a.lon } }, ...u].slice(0, 50));
        setHeldAddr({ ...a, lat: e.latLng.lat(), lon: e.latLng.lng(), placed_just_now: true });
      } catch (err: any) {
        // Put it back where it was. A dot resting somewhere the database does not agree with is
        // the worst possible outcome for a screen whose whole job is showing you the truth.
        setMsg(String(err?.message || err));
        const a = density === "every"
          ? await api<HeldAddr>(`/geocode/address-at?id=${denseIdsRef.current[i]}`).catch(() => null)
          : ptAt(i);
        if (a) denseRef.current?.moveTo(i, a.lat, a.lon);
      }
    });

    return () => {
      map.setOptions({ draggable: true });
      google.maps.event.removeListener(down);
      google.maps.event.removeListener(move);
      google.maps.event.removeListener(up);
    };
  }, [view, density, scope, mapReady]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function undoLast() {
    const top = undo[0]; if (!top) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ lat: number; lon: number; restored_method: string | null }>(
        "/geocode/address-unplace",
        { method: "POST", body: JSON.stringify({ placement_id: top.placementId }) });
      // The server says where the pin ENDED UP -- which is the placement this one superseded, not
      // necessarily where the drag started. Moving the dot back to `from` would be a guess that is
      // wrong whenever a machine pin was underneath.
      denseRef.current?.moveTo(top.index, r.lat, r.lon);
      setUndo((u) => u.slice(1));
      setHeldAddr(null);
      setMsg(`Put ${top.label} back${r.restored_method ? ` on the ${r.restored_method} pin` : ""}.`);
    } catch (e: any) { setMsg(String(e?.message || e)); }
    finally { setBusy(false); }
  }

  // ── ASK GOOGLE WHERE THIS ADDRESS IS ────────────────────────────────────────────────────────
  //
  // Dragging is the correction path and it is the right one when the pin is on the wrong roof.
  // Caleb, on the case it cannot answer: "when it's miles and miles away some other place in the
  // map I don't know how I'm going to fix this geocode conveniently". You cannot drag to a place
  // you cannot find, and the screen was requiring him to already know the answer before it would
  // help him get there.
  //
  // THE GEOCODER IS THE MAPS SDK ALREADY ON THIS PAGE, not a new backend route. Same key, same
  // referrer restriction, one library import, nothing to deploy server-side. It bills as Geocoding
  // API per call, which is why it is a BUTTON: a screen that draws 120,000 doors must not quietly
  // ask a third party about every one of them.
  //
  // TEXAS, NOT THE COUNTY. Without a restriction "1 RIVER RD" returns the whole country. With a
  // COUNTY restriction the answer this feature exists to find would be filtered out -- the case
  // that prompted it is a Travis pin whose only real address is in Hays.
  async function runLookup(key: string, q: string, from: { lat: number; lon: number } | null) {
    const google = googleRef.current;
    const text = q.trim();
    if (!text) return;
    if (!google) {
      setLookup({ key, q: text, hits: null, running: false,
                  err: "The map has not finished loading yet." });
      return;
    }
    setLookup({ key, q: text, hits: null, err: null, running: true });
    try {
      const lib: any = await google.maps.importLibrary("geocoding");
      const res = await new lib.Geocoder().geocode({
        address: text,
        componentRestrictions: { country: "US", administrativeArea: "TX" },
      });
      // GOOGLE'S ORDER IS KEPT. Sorting by distance would rank the answers by how close each one
      // is to the pin we already believe is wrong, which is exactly the wrong ordering.
      const hits: GeoHit[] = (res.results || []).map((r: any) => {
        const lat = r.geometry.location.lat(), lon = r.geometry.location.lng();
        return { label: r.formatted_address, lat, lon,
                 precision: String(r.geometry.location_type || ""),
                 partial: !!r.partial_match,
                 m: from ? metresBetween(from.lat, from.lon, lat, lon) : null,
                 dir: from ? bearing(from.lat, from.lon, lat, lon) : null };
      });
      setLookup({ key, q: text, hits, err: null, running: false });
    } catch (e: any) {
      // ZERO_RESULTS arrives as a REJECTION, not an empty array -- and it is far rarer than it
      // sounds. See the measurements above GeoHit: Google usually degrades to a partial match on
      // the road or the state rather than admitting it has nothing.
      const code = String(e?.code || e?.message || e);
      if (code.includes("ZERO_RESULTS")) setLookup({ key, q: text, hits: [], err: null, running: false });
      else setLookup({ key, q: text, hits: null, err: code, running: false });
    }
  }

  // THE ANSWERS, ON THE MAP. A list of formatted addresses does not answer "where is this"; the
  // lettered pins the conflict card already uses do, and the question is the same one.
  const lookRef = useRef<any[]>([]);
  useEffect(() => {
    const google = googleRef.current, map = mapRef.current;
    lookRef.current.forEach((m) => { m.map = null; });
    lookRef.current = [];
    if (!google || !map || !lookup?.hits?.length) return;
    lookup.hits.forEach((h, n) => {
      const el = document.createElement("div");
      el.style.cssText = "width:26px;height:26px;border-radius:50%;color:#101318;"
        + `background:${geoIsPlaceable(h.precision) ? "#8bd3ff" : "#79818d"};`
        + "font:700 13px system-ui;display:flex;align-items:center;justify-content:center;"
        + "border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.5)";
      el.textContent = String.fromCharCode(65 + n);
      lookRef.current.push(new google.maps.marker.AdvancedMarkerElement({
        position: { lat: h.lat, lng: h.lon }, map, content: el,
        title: `${h.label} — ${GEO_PRECISION[h.precision] || h.precision}`,
      }));
    });
    return () => { lookRef.current.forEach((m) => { m.map = null; }); lookRef.current = []; };
  }, [lookup, mapReady]);

  // SHOW ME WHERE IT IS. Google's answer can be a hundred miles off screen, which is the whole
  // point of the case this was built for -- an answer you cannot see is not one you can check.
  function flyTo(lat: number, lon: number) {
    const map = mapRef.current; if (!map) return;
    map.panTo({ lat, lng: lon }); map.setZoom(19);
  }

  // ── ACCEPTING AN ANSWER, FROM THE MAP VIEW ──────────────────────────────────────────────────
  //
  // Deliberately the SAME call the drag handler makes: a human placement, sticky against every
  // automated pass, on the same undo stack. Only the note differs, because six months from now
  // "why is this pin here" has a different answer for a drag and for a Google result.
  async function placeHeld(lat: number, lon: number, note: string) {
    if (!heldAddr || busy) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ placement_id: number }>("/geocode/address-place", {
        method: "POST",
        body: JSON.stringify({ fips: heldAddr.fips, addr_key: heldAddr.addr_key, lat, lon, note }),
      });
      // -1 when the pin was reached from somewhere that has no dot on the canvas. moveTo ignores
      // an out-of-range index, so undo stays safe rather than needing its own special case.
      denseRef.current?.moveTo(heldIdx ?? -1, lat, lon);
      setUndo((u) => [{ placementId: r.placement_id, index: heldIdx ?? -1,
                        label: heldAddr.full_addr || heldAddr.addr_key,
                        from: { lat: heldAddr.lat, lon: heldAddr.lon } }, ...u].slice(0, 50));
      setHeldAddr({ ...heldAddr, lat, lon, placed_just_now: true });
      setLookup(null);
      flyTo(lat, lon);
      mapQ.reload();
    } catch (e: any) { setMsg(String(e?.message || e)); }
    finally { setBusy(false); }
  }

  // THE VERDICT, FROM THE MAP. All three dispositions existed already -- on the queue card, and
  // only there. So from the county map, which is where a pin that is obviously in the wrong place
  // is actually NOTICED, there was no way to say "there is no such address". Caleb: "I need to be
  // able to mark that as a non existent address". The button was written; it was unreachable.
  async function judgeHeld(status: string) {
    if (!heldAddr || busy) return;
    setBusy(true); setMsg(null);
    try {
      await api("/geocode/address-review", {
        method: "POST",
        body: JSON.stringify({ addr_key: heldAddr.addr_key, fips: heldAddr.fips, status }),
      });
      setMsg(`${heldAddr.full_addr || heldAddr.addr_key} — ${REVIEW_LABEL[status] || status}. `
             + `Nothing was deleted; reopen it from Reviewed.`);
      setHeldAddr({ ...heldAddr, review_status: status });
      setLookup(null);
      counties.reload(); mapQ.reload();
    } catch (e: any) { setMsg(String(e?.message || e)); }
    finally { setBusy(false); }
  }

  // CLICKING A DOT NO LONGER LEAVES THE MAP, and that is the whole change. It used to call
  // pickFromMap, which opens the judging card -- so reading one pin cost a screen change, and
  // coming back put you at the top of a queue rather than where you were looking. On a screen whose
  // value is seeing a thousand doors at once, navigating away per door is the opposite of the job.
  //
  // The mousedown handler above already hit-tests, fills the address panel and starts a drag, in
  // BOTH densities now. So there is nothing left for a click listener to do that is not already
  // done better, and a second hit test fighting the first is how a click ends up both reading a pin
  // and moving it. pickFromMap stays -- search results still open a card, which is where a card
  // makes sense: you asked for one address by name.

  // A COORDINATE YOU CANNOT SEE IS NOT A PIN. Two decimal readouts under a form are not an answer
  // to "is it on the right roof", which is the only question this whole screen exists to settle.
  // Green, because the red marker already means "the pin this address has" and these are different
  // claims. Draggable for the same reason the other one is: the first click is rarely exact.
  useEffect(() => {
    const google = googleRef.current, map = mapRef.current;
    if (createMarkerRef.current) { createMarkerRef.current.map = null; createMarkerRef.current = null; }
    if (!google || !map || !creating || creating.lat == null || creating.lon == null) return;
    const dot = document.createElement("div");
    dot.style.cssText = "width:18px;height:18px;border-radius:50%;background:#2ecc71;"
      + "border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.5);cursor:grab";
    const m = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: creating.lat, lng: creating.lon }, map, content: dot, gmpDraggable: true,
      title: "the address being added",
    });
    m.addListener("dragend", (ev: any) => {
      const lat = ev.latLng.lat(), lon = ev.latLng.lng();
      setCreating((c) => (c ? { ...c, lat, lon } : c));
      findNearbyRef.current?.(lat, lon);
    });
    createMarkerRef.current = m;
    return () => { m.map = null; createMarkerRef.current = null; };
    // DEPS ARE THE COORDINATE AND NOTHING ELSE. Including the typed text would tear down and
    // rebuild the marker on every keystroke; including `creating != null` is a computed expression
    // the hooks lint rejects, and is redundant -- clearing `creating` makes lat undefined, which
    // differs from the last number and runs the cleanup on its own.
  }, [creating?.lat, creating?.lon]);

  // NEAREST FIRST, FROM MEMORY. metresBetween already exists at the bottom of this file for the
  // pin-moved readout; reusing it means one definition of distance on this screen.
  //
  // 150 m IS THE NUMBER THIS SYSTEM ALREADY USES for "close enough to be the same door":
  // record_placement's c_max_building_move_m and building_place.MAX_MOVE_M draw the same line, and
  // beyond it an address keeps the county's coordinate rather than being moved onto a building. A
  // third number here would be a third meaning of the same idea, which is how this repo has been
  // bitten before. 60 m was mine and it was too tight: the split-lot case Caleb runs into -- two
  // addresses stacked on one roof while the house beside them has none -- sits comfortably past it.
  const NEAR_M = 150;

  // WHY EACH REFUSAL MATTERS TO SOMEBODY ABOUT TO ADD AN ADDRESS. These are 0167's own reasons, not
  // a restatement: the parcel pass already decided this and recorded why, with UNITS STRIPPED before
  // deciding, so an apartment block is not mistaken for a split lot. Reading the row is the whole
  // feature; a client-side re-derivation would be a second copy of a rule that is already measured.
  const CONFLICT_SAYS: Record<string, string> = {
    would_stack_on_another_pin:  "its pin would land on another street's pin",
    multiple_streets_one_parcel: "two streets claim one lot — a split lot",
    moves_too_far:               "its building is over 150 m away, so the pin stayed on the lot",
    multiple_parcels_claim:      "two parcels more than 25 m apart claim this address",
    ambiguous_address_key:       "its key names more than one street",
  };

  async function findNearby(lat: number, lon: number) {
    setNearby(null);
    try {
      if (density === "every") {
        const la = denseLatRef.current, lo = denseLonRef.current, ids = denseIdsRef.current;
        const found: { i: number; m: number }[] = [];
        for (let i = 0; i < la.length; i++) {
          const m = metresBetween(lat, lon, la[i], lo[i]);
          if (m <= NEAR_M) found.push({ i, m });
        }
        found.sort((a2, b2) => a2.m - b2.m);
        // EIGHT, NEAREST FIRST. At 150 m a dense subdivision has more neighbours than anyone will
        // read; the nearest are the ones that could be the house you are standing at, and the count
        // below says how many were not listed so the list never implies it is complete.
        const top = found.slice(0, 8);
        const rows = await Promise.all(top.map(async (f) => {
          const d = await api<any>(`/geocode/address-at?id=${ids[f.i]}`).catch(() => null);
          return d ? { id: ids[f.i], full_addr: d.full_addr || d.addr_key, addr_key: d.addr_key,
                       m: f.m, post_comm: d.post_comm ?? null, post_code: d.post_code ?? null,
                       quality: d.geocode_quality ?? null,
                       conflict: d.conflict_reason ?? null } : null;
        }));
        const clean = rows.filter(Boolean) as NonNullable<typeof rows[number]>[];
        setNearby(clean);
        // OFFER THE NEIGHBOUR'S TOWN AND ZIP. Nobody standing at a door knows the ZIP, and the
        // house next to it already does. Only fills what is still blank, so a correction sticks.
        const src = clean.find((r) => r.post_comm || r.post_code);
        if (src) setCreating((c) => (c ? {
          ...c,
          city: c.city || src.post_comm || "",
          zip: c.zip || src.post_code || "",
        } : c));
      } else {
        // "What is left to work" carries its text already, so no lookup — but it is a FILTERED
        // layer, so absence here is not evidence of absence. The card says so rather than implying
        // the spot is clear.
        const near = ptsRef.current
          .map((p) => ({ p, m: metresBetween(lat, lon, p.lat, p.lon) }))
          .filter((x) => x.m <= NEAR_M).sort((a2, b2) => a2.m - b2.m).slice(0, 8);
        setNearby(near.map((x) => ({ id: null, full_addr: x.p.full_addr || x.p.addr_key,
                                     addr_key: x.p.addr_key, m: x.m,
                                     post_comm: null, post_code: null,
                                     quality: x.p.geocode_quality ?? null,
                                     conflict: x.p.conflict_reason ?? null })));
      }
    } catch { setNearby([]); }
  }

  findNearbyRef.current = findNearby;

  async function createAddress() {
    if (!creating || scope === "" || busy) return;
    if (creating.lat == null || creating.lon == null) {
      setMsg("Click the roof on the map first — an address without a pin is not worth adding."); return;
    }
    if (creating.full_addr.trim().length < 3) { setMsg("Type the address as the county would write it."); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ addr_key: string; sticky: boolean }>("/geocode/address-create", {
        method: "POST",
        body: JSON.stringify({
          fips: Number(scope), county: chosen?.county || "",
          full_addr: creating.full_addr.trim(), lat: creating.lat, lon: creating.lon,
          unit: creating.unit.trim() || null,
          city: creating.city.trim() || null,
          zip: creating.zip.trim() || null,
        }),
      });
      setCreating(null); setNearby(null); setHits(null); setQ("");
      // SAY WHETHER IT IS STICKY, because it is not always. On a key that names two streets the
      // address is added and the pin is NOT written -- no machine can move it either, but the
      // person should know which of the two they got rather than assume the usual one.
      setMsg(r.sticky
        ? `Added ${r.addr_key}. Your pin stays — no automated pass will move it.`
        : `Added ${r.addr_key}. Its key names more than one street, so the pin was left on your `
          + `coordinate without a placement; nothing automated will move it either.`);
      counties.reload(); mapQ.reload();
    } catch (e: any) { setMsg(String(e?.message || e)); }
    finally { setBusy(false); }
  }

  function pickFromMap(p: MapPt) {
    // TAKE ME TO THE PIN, WHEREVER IT IS. Caleb, on the first statewide search he ran: "it gave me
    // the results, but nothing allows me to click on it to be taken to the pin".
    //
    // The card and the map are both inside `scope !== ""`, so with no county chosen there was
    // nothing on screen for a click to open into -- the verdict was set, the view switched, and the
    // page went on saying "Pick a county to work". The same click from inside Travis onto a Hays
    // row would have opened the card over Travis's map.
    //
    // So the row's county comes with it. Switching counties is what "go to that pin" MEANS once a
    // result can be from anywhere, and it is the one thing the person cannot do for themselves
    // without first reading the county off the row and finding it in a 254-entry select.
    //
    // `picked` survives this: only the select's own onChange clears it, and nothing here is that.
    // `hits` survives too, deliberately -- the other rows are still the comparison you were making.
    if (p.fips != null && p.fips !== fips) { setFips(p.fips); setI(0); }
    setPicked({
      // THE ROW'S OWN COUNTY, NOT THE ONE ON SCREEN. Before 0247 every search result was in the
      // county being worked, so `Number(scope)` was always right. Now it is not: opening the Hays
      // house from inside Travis and then judging or placing it would have written the verdict
      // against 48453, which is a different address, or none.
      addr_key: p.addr_key, fips: p.fips ?? Number(scope), county: p.county ?? null,
      full_addr: p.full_addr,
      lat: p.lat, lon: p.lon, geocode_quality: p.geocode_quality,
      parcel_verdict: null, parcel_addr_count: null, parcel_area_m2: null,
      county_pct_own: null, credibility: null,
      // CARRY THE CANDIDATES. Dropping them here opened a card that stated a contradiction and gave
      // you nothing to choose between -- which is the whole thing they exist for.
      conflict_reason: p.conflict_reason, conflict_detail: null,
      conflict_candidates: p.conflict_candidates,
      // CARRY THE VERDICT IT ALREADY HAS. Search returns judged rows too -- greyed, tagged
      // "judged" -- and opening one used to produce a card with three disposition buttons and no
      // sign that the question had already been answered.
      review_status: p.review_status,
    });
    setView("queue");
  }

  async function runSearch() {
    if (q.trim().length < 2) return;
    setBusy(true); setMsg(null);
    try {
      // NO COUNTY IS A VALID SEARCH, and it is the one somebody texting you an address lands in.
      // The server treats a missing fips as "key pass only, statewide"; requiring a county first
      // is requiring the answer before the question.
      const r = await api<{ results: MapPt[] }>(
        `/geocode/address-search?q=${encodeURIComponent(q.trim())}`
        + (scope !== "" ? `&fips=${scope}` : ""));
      setHits(r.results);
      // NOT A DEAD END ANY MORE. "Nothing matches" was the entire answer here, and it is the wrong
      // one when the reason nothing matches is that TxGIO has not published the house yet -- which
      // is exactly the case a canvasser standing in front of it needs to fix. Offer the add rather
      // than announcing the absence.
      //
      // ONLY WITH A COUNTY CHOSEN. create_address writes into one, and guessing which from a typed
      // string is exactly the mistake that put a Hays road on a Travis lot.
      if (r.results.length === 0 && scope !== "")
        setCreating({ full_addr: q.trim(), unit: "", city: "", zip: "", lat: null, lon: null });
      // ON THE BIG MAP, SEARCH MOVES THE MAP. A list of hits under a 120,000-dot map is a second
      // place to look; flying to the first one puts the answer where the eye already is. The list
      // stays for the other matches.
      else if (density === "every" && mapRef.current && r.results[0].lat != null) {
        mapRef.current.panTo({ lat: r.results[0].lat, lng: r.results[0].lon });
        mapRef.current.setZoom(20);
      }
    } catch (e: any) { setMsg(String(e?.message || e)); }
    finally { setBusy(false); }
  }

  // Which lettered candidate is currently armed, so the commit button can name it.
  const chosenLetter = (() => {
    const cs = cur?.conflict_candidates;
    if (!dragged || !cs) return null;
    const i = cs.findIndex((c) => Math.abs(c.lat - dragged.lat) < 1e-9
                               && Math.abs(c.lon - dragged.lon) < 1e-9);
    return i >= 0 ? String.fromCharCode(65 + i) : null;
  })();

  const advance = useCallback(() => {
    setDone((d) => d + 1);
    setI((n) => n + 1);
    // Refill well before the end so the queue never appears empty mid-session -- an empty list here
    // would read as "county finished", which is exactly the wrong thing to say by accident.
    if (i >= rows.length - 5) reload();
  }, [i, rows.length, reload]);

  // WHERE THE CARD CAME FROM DECIDES WHAT HAPPENS AFTER THE VERDICT.
  //
  // Judging the queue's current row means "next": the queue is a pile you are working down, and
  // the next row is the reason you are on this screen. Judging a row you SEARCHED for is the
  // opposite -- the errand is finished, and the queue's next row is an address you never asked to
  // see. Caleb, after marking a searched address non-existent: "this is what popped up, so clearly
  // there is a transition error here that makes this unworkable". What popped up was 21461 COYOTE
  // TRL, under a search panel still describing 3700 MT GAINOR RD.
  const cameFromSearch = () => picked != null;

  async function judge(status: string) {
    if (!cur || busy) return;
    const stay = cameFromSearch();
    // "clear" is the put-it-back button, not a verdict. It has no REVIEW_LABEL and must leave the
    // row looking unjudged, or the card would sit there claiming a status it just removed.
    const verdict = status === "clear" ? null : status;
    setBusy(true); setMsg(null);
    try {
      await api(`/geocode/address-review`, {
        method: "POST",
        body: JSON.stringify({ addr_key: cur.addr_key, fips: cur.fips, status }),
      });
      patch((p) => ({ addresses: p.addresses.filter((a) => a.addr_key !== cur.addr_key) }));
      setI((n) => Math.min(n, Math.max(0, rows.length - 2)));
      setDone((d) => d + 1);
      if (stay) {
        // STAY ON WHAT YOU JUDGED, SHOWING THE VERDICT -- exactly what judgeHeld already does from
        // the map. The person leaves by clicking another result or Clear, which are both decisions
        // they made rather than one the screen made for them.
        setPicked({ ...cur, review_status: verdict, placed_just_now: false });
        setMsg(verdict
          ? `${cur.full_addr || cur.addr_key} — ${REVIEW_LABEL[verdict] || verdict}. `
            + `Nothing was deleted; reopen it from Reviewed.`
          : `${cur.full_addr || cur.addr_key} — back in the queue.`);
      } else {
        setPicked(null);
      }
      // MARK IT IN THE RESULTS, DO NOT REMOVE IT. Filtering the judged row out made the panel
      // re-describe itself: with the only in-county match gone, SearchHits fell through to
      // "Nothing in TRAVIS -- but this address exists elsewhere in Texas", which is a claim about
      // the SEARCH being produced here by your own verdict, seconds after the search found the
      // thing. Marking leaves the list reading exactly as a fresh search of the same text returns
      // it -- greyed dot, "judged" -- which is the test for whether this is right.
      setHits((h) => hitsAfterVerdict(h, cur.addr_key, cur.fips, verdict));
      counties.reload(); mapQ.reload();
    } catch (e: any) { setMsg(String(e?.message || e)); }
    finally { setBusy(false); }
  }

  // `at` OVERRIDES THE DRAGGED PIN, for a coordinate that came from the lookup panel rather than
  // from the map. NEVER wire this to onClick directly -- a click event would arrive as `at` and the
  // placement would be written at NaN. Every call site passes its own arrow function.
  async function placeHere(at?: { lat: number; lon: number }, note?: string) {
    const to = at ?? dragged;
    if (!cur || !to || busy) return;
    const stay = cameFromSearch();
    setBusy(true); setMsg(null);
    try {
      await api(`/geocode/address-place`, {
        method: "POST",
        body: JSON.stringify({ addr_key: cur.addr_key, fips: cur.fips, lat: to.lat, lon: to.lon,
                               note: note ?? null }),
      });
      // Placing a pin IS the judgement -- the address leaves the queue without a separate verdict.
      patch((p) => ({ addresses: p.addresses.filter((a) => a.addr_key !== cur.addr_key) }));
      setDragged(null); setDone((d) => d + 1);
      setLookup(null);
      // SAME TRANSITION RULE AS `judge`. Placing a pin on an address you searched for is the errand
      // finishing, not a cue to hand you the next queue row.
      if (stay) {
        setPicked({ ...cur, lat: to.lat, lon: to.lon, placed_just_now: true });
        setMsg(`${cur.full_addr || cur.addr_key} — pin placed. It is permanent; no automatic pass `
               + `can move it.`);
      } else {
        setPicked(null);
      }
      // Mark, do not remove -- see `judge`. A placed row reads "your pin" in the list.
      setHits((h) => hitsAfterPlacement(h, cur.addr_key, cur.fips, to.lat, to.lon));
      counties.reload(); mapQ.reload();
    } catch (e: any) { setMsg(String(e?.message || e)); }
    finally { setBusy(false); }
  }

  // Keyboard: the whole point is working an area quickly. 1-4 judge, Enter commits a dragged pin.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === "INPUT" || (e.target as HTMLElement)?.tagName === "SELECT") return;
      const d = DISPOSITIONS.find((x) => x.key === e.key);
      // 1/2/3 JUDGE WHATEVER IS IN FRONT OF YOU. On the queue that is the current card; on the map
      // it is the pin you just clicked. Before this the keys were bound to `judge`, which reads the
      // queue's current row -- so on the map they silently did nothing at all.
      if (d) { e.preventDefault(); if (view === "queue") judge(d.status); else judgeHeld(d.status); }
      else if (e.key === SHORTCUT_COMMIT.key && dragged) { e.preventDefault(); placeHere(); }
      else if (e.key === SHORTCUT_SKIP.key) { e.preventDefault(); advance(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });   // re-bound each render so the handlers close over the current address

  // FROM THE COUNTY LIST, not a second endpoint. The first version read summary.suspect_addresses
  // and summary.total_addresses -- fields triage_summary has never returned -- so the progress bar
  // silently never rendered. It was reading undefined and hiding itself, which is why it looked
  // like a layout choice rather than a bug.
  const remaining = chosen?.remaining ?? null;
  const total = chosen?.suspects ?? null;
  const pct = chosen?.pct_done ?? null;

  return (
    <div className="wrap">
      <div className="row" style={{ marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        {/* NO <h2> HERE. This was a top-level tab and carried its own title; inside the Vault shell
            that drew two headings with two subtitles stacked on top of each other. The shell names
            the screen now. The controls below stay exactly where they were. */}
        {/* A COUNTY IS PICKED BY NAME. The first version asked for a raw FIPS code -- nobody knows
            Kaufman is 48257, and a tool you cannot start using is not a tool. */}
        <Select hint="County" value={fips === "" ? "" : String(fips)} disabled={countyList.length === 0}
                onChange={(v) => { setFips(v === "" ? "" : Number(v));
                                   setI(0); setDone(0); setPicked(null); setHits(null); setView("map"); }}
                options={[["", counties.query.status === "error" ? "Could not load counties"
                              : isFetching(counties.query) ? "Loading counties…" : "Choose a county…"] as [string, string],
                          ...countyList.map((c) => [String(c.fips),
                            `${c.county} — ${c.remaining.toLocaleString()} left${c.pct_done != null ? ` (${c.pct_done}% done)` : ""}`] as [string, string])]} />
        <select className="ctl" style={{ maxWidth: 190 }} value={order}
                onChange={(e) => { setOrder(e.target.value as any); setI(0); }}>
          <option value="worst_first">Worst first</option>
          <option value="grouped">Whole streets together</option>
        </select>
        {scope !== "" && (
          <>
            <div className="row" style={{ gap: 0 }}>
              <button className={"btn sm" + (view === "map" ? " accent" : "")}
                      onClick={() => { setView("map"); setPicked(null); }}>County map</button>
              <button className={"btn sm" + (view === "queue" ? " accent" : "")}
                      onClick={() => { setView("queue"); setPicked(null); }}>Work the queue</button>
              <button className={"btn sm" + (view === "reviewed" ? " accent" : "")}
                      onClick={() => { setView("reviewed"); setPicked(null); }}>Reviewed</button>
            </div>
          </>
        )}
        {/* OUTSIDE THE COUNTY BLOCK ON PURPOSE. Until 0247 this form only existed once a county was
            chosen, because the search could only work inside one. It can now answer "somebody sent
            me this address, where is it" with no county picked at all, and that question arrives
            before you know which county to pick. */}
        <form onSubmit={(e) => { e.preventDefault(); runSearch(); }} className="row" style={{ gap: 4 }}>
          <input className="ctl" style={{ maxWidth: 260 }} value={q} name="addr-search"
                 aria-label="Find an address"
                 placeholder={scope === "" ? "Paste an address — any county"
                                           : "Find an address or street"}
                 onChange={(e) => setQ(e.target.value)} />
          <button className="btn sm" type="submit" disabled={busy || q.trim().length < 2}>Find</button>
          {hits && <button className="btn sm" type="button" onClick={() => { setHits(null); setQ(""); }}>Clear</button>}
        </form>
        {done > 0 && <span className="muted">{done} judged this session</span>}
        <span style={{ marginLeft: "auto" }}><ShortcutHelp /></span>
      </div>

      {scope === "" && (
        <div className="card" style={{ padding: 18 }}>
          <p style={{ marginTop: 0 }}>Pick a county to work.</p>
          <p className="muted" style={{ marginBottom: 0 }}>
            Geocodes get cleaned ahead of a specific race, so the queue is scoped to the turf you are
            about to cut. <strong>Worst first</strong> leads on county credibility — a suspect in a county
            whose parcel file is trustworthy is a real outlier, one in a county scoring 28% is mostly
            telling you the parcel file is poor. <strong>Whole streets together</strong> puts a road in one
            block, because neighbouring addresses usually fail for the same reason.
          </p>
        </div>
      )}

      {/* An empty list must never be confused with a failed request (empty-vs-error guard). */}
      {query.status === "error" && (
        <div className="err" style={{ marginBottom: 10 }}>
          Could not load the queue: {String(query.error)} <button className="btn sm" onClick={reload}>Retry</button>
        </div>
      )}
      {msg && <div className="err" style={{ marginBottom: 10 }}>{msg}</div>}

      {scope !== "" && pct != null && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <strong>{chosen?.county || `County ${scope}`}</strong>
            <span className="muted">
              {remaining?.toLocaleString()} left to judge of {total?.toLocaleString()} flagged
              {" · "}
              {/* ── SAY WHETHER WE HAVE PARCEL DATA, ALWAYS ────────────────────────────────────
                  This clause used to be gated on `pct_own != null`, so a county we have NOT
                  measured against parcels rendered NOTHING here -- and an absent sentence reads
                  exactly like a sentence nobody thought to write. A reviewer judging pins in one
                  of those counties had no way to tell "the parcel check agrees with these pins"
                  from "there is no parcel check".

                  county_credibility() already distinguishes the three states and the UI was
                  throwing two of them away: 9 counties come back 'unmeasured' with a NULL
                  pct_own, and everything else carries high/good/mixed/low with a percentage.

                  'unmeasured' is NOT the same claim as "this county has no parcel file" -- it is
                  also what a county below the 1,000-point credibility floor returns, which is why
                  this says what we know rather than inferring which of the two it is. */}
              {parcelDataLabel(chosen?.credibility ?? null, chosen?.pct_own ?? null)}
            </span>
          </div>
          <div style={{ height: 8, background: "#2a2f3a", borderRadius: 4, marginTop: 8, overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: pct >= 99 ? "#3ddc84" : "#4c8dff" }} />
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            {pct >= 99
              ? "This county is clean — safe to sell as walkable turf."
              : `${pct}% settled — ${chosen?.placed.toLocaleString()} placed automatically, `
                + `${chosen?.reviewed.toLocaleString()} judged by you, `
                + `${chosen?.conflicts.toLocaleString()} the pass declined.`}
          </div>
        </div>
      )}

      {scope !== "" && query.status !== "error" && rows.length === 0 && !isFetching(query) && (
        <div className="card" style={{ padding: 18 }}>
          <strong>Nothing left in this county.</strong>
          <div className="muted" style={{ marginTop: 6 }}>
            Every flagged address here has been judged or settled automatically.
          </div>
        </div>
      )}

      {creating && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
            <strong>Not in the vault</strong>
            <button className="btn sm" onClick={() => { setCreating(null); setNearby(null); setMsg(null); }}>Cancel</button>
          </div>
          <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
            Nothing in {chosen?.county || "this county"} matches this. If the house is really there,
            add it — the 9-1-1 file has not published it yet. It will survive the next county
            refresh, and when the state does publish it your pin carries over.
          </p>
          <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
            <strong>City and ZIP tell this door from a namesake.</strong> The household key drops the
            street type, so <em>146 Honeysuckle Road</em> and <em>146 Honeysuckle Lane</em> are one
            key even 50 km apart; the street pass also groups a road by its town. Five digits is all
            that is kept — no plus-four, ever — and clicking the roof fills both in from the nearest
            neighbour, so you should rarely have to type them.
          </p>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            <input className="ctl" style={{ minWidth: 240 }} value={creating.full_addr}
                   aria-label="The address, as the county would write it"
                   placeholder="305 W FRANKLIN AVE"
                   onChange={(e) => setCreating((c) => (c ? { ...c, full_addr: e.target.value } : c))} />
            <input className="ctl" style={{ maxWidth: 96 }} value={creating.unit}
                   aria-label="Unit or apartment, if any" placeholder="Apt B"
                   onChange={(e) => setCreating((c) => (c ? { ...c, unit: e.target.value } : c))} />
            <input className="ctl" style={{ maxWidth: 140 }} value={creating.city}
                   aria-label="Postal city" placeholder="City"
                   onChange={(e) => setCreating((c) => (c ? { ...c, city: e.target.value } : c))} />
            <input className="ctl" style={{ maxWidth: 96 }} value={creating.zip}
                   aria-label="Five-digit ZIP code" placeholder="ZIP (5)" inputMode="numeric"
                   onChange={(e) => setCreating((c) => (c ? { ...c, zip: e.target.value } : c))} />
            <span className="muted" style={{ fontSize: 12 }}>
              {creating.lat != null
                ? `pin at ${creating.lat.toFixed(6)}, ${creating.lon!.toFixed(6)} — drag the green dot to adjust`
                : "click the roof on the map to set the pin"}
            </span>
            <button className="btn sm accent" disabled={busy || creating.lat == null}
                    onClick={createAddress}>Add this address</button>
          </div>

          {/* THE NEAR MISS, SHOWN BEFORE IT IS MADE. An exact key clash is refused by the database;
              this catches the one it cannot -- a different spelling of the same door, which keys
              differently and becomes a second row on one roof. Naming the neighbours is the whole
              mechanism: the operator recognises their own house in the list. */}
          {creating.lat != null && nearby != null && nearby.length > 0 && (
            <div style={{ marginTop: 10, borderTop: "1px solid rgba(128,128,128,.25)", paddingTop: 8 }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                <strong>{nearby.length}{nearby.length === 8 ? "+" : ""} address
                {nearby.length === 1 ? "" : "es"} already within {NEAR_M} m of this pin.</strong>{" "}
                Nearest first. If one of these is the house you are looking at, open it and move its
                pin instead — adding a second spelling puts two pins on one roof. A line in amber is
                one the parcel pass already refused to place, and says why.
              </div>
              <div style={{ display: "grid", gap: 4 }}>
                {nearby.map((n) => (
                  <button key={n.addr_key + n.m} className="btn sm"
                          style={{ justifyContent: "flex-start", height: "auto",
                                   flexDirection: "column", alignItems: "flex-start", gap: 2,
                                   paddingTop: 6, paddingBottom: 6 }}
                          onClick={() => {
                            setCreating(null); setNearby(null); setQ(n.full_addr);
                            setPicked({
                              addr_key: n.addr_key, fips: Number(scope), county: null,
                              full_addr: n.full_addr, lat: null, lon: null, geocode_quality: "",
                              parcel_verdict: null, parcel_addr_count: null, parcel_area_m2: null,
                              county_pct_own: null, credibility: null, conflict_reason: null,
                              conflict_detail: null, conflict_candidates: null,
                            });
                            setView("queue");
                          }}>
                    <span>
                      {n.full_addr}
                      <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>
                        {n.m < 1 ? "on this spot" : `${n.m.toFixed(0)} m away`}
                      </span>
                    </span>
                    {/* THE REASON THE PARCEL PASS ALREADY GAVE, ahead of the grade, because it is
                        the more specific fact: 'two streets claim one lot' tells you what to do,
                        'parcel' only tells you where the pin came from. */}
                    {n.conflict && (
                      <span style={{ fontSize: 11, color: "#e0a33e" }}>
                        {CONFLICT_SAYS[n.conflict] || n.conflict.replace(/_/g, " ")}
                      </span>
                    )}
                    {!n.conflict && n.quality && n.quality !== "rooftop_snapped"
                      && n.quality !== "rooftop_verified" && (
                      <span className="muted" style={{ fontSize: 11 }}>
                        pin never placed on a building ({n.quality}) — it may be far from its door
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
          {creating.lat != null && nearby != null && nearby.length === 0 && (
            <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              {density === "every"
                ? `Nothing else within ${NEAR_M} m of this pin.`
                : `Nothing flagged within ${NEAR_M} m — but this layer only draws addresses that `
                  + `need work, so switch to every door to be sure.`}
            </div>
          )}
        </div>
      )}

      <SearchHits hits={hits} fips={fips} countyName={chosen?.county ?? null}
                  onPick={pickFromMap} />

      {scope !== "" && view === "reviewed" && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div className="row" style={{ gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
            {[["", "All"], ["verified", "Looks right"], ["unresolved", "Couldn't tell"],
              ["no_such_address", "Doesn't exist"]].map(([v, label]) => (
              <button key={v} className={"btn sm" + (revStatus === v ? " accent" : "")}
                      onClick={() => setRevStatus(v)}>
                {label}
                {v && queryData(rev.query)?.counts?.[v] != null
                  && ` (${queryData(rev.query)!.counts[v].toLocaleString()})`}
              </button>
            ))}
            <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>
              Click any of these to open it again — a judgement is never final.
            </span>
          </div>
          {rev.query.status === "error" && (
            <div className="err">Could not load reviewed addresses: {String(rev.query.error)}{" "}
              <button className="btn sm" onClick={rev.reload}>Retry</button></div>
          )}
          {(queryData(rev.query)?.reviewed.length ?? 0) === 0 && !isFetching(rev.query)
            && rev.query.status !== "error" && (
            <div className="muted">Nothing judged in this county yet.</div>
          )}
          <div style={{ display: "grid", gap: 4, maxHeight: 420, overflowY: "auto" }}>
            {(queryData(rev.query)?.reviewed ?? []).map((r) => (
              <button key={r.addr_key} className="btn sm" style={{ justifyContent: "flex-start" }}
                      onClick={() => pickFromMap(r)}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: ptColour(r),
                               display: "inline-block", marginRight: 8 }} />
                {r.full_addr || r.addr_key}
                <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>
                  {REVIEW_LABEL[r.status] || r.status}
                  {r.reviewed_at ? ` · ${new Date(r.reviewed_at).toLocaleDateString()}` : ""}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {scope !== "" && view === "map" && (
        <div className="card" style={{ padding: 10, marginBottom: 12 }}>
          <div className="row" style={{ gap: 6, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
            <button className={"btn sm" + (density === "problems" ? " accent" : "")}
                    onClick={() => setDensity("problems")}>What is left to work</button>
            <button className={"btn sm" + (density === "every" ? " accent" : "")}
                    onClick={() => setDensity("every")}>Every door</button>
            <span className="muted" style={{ fontSize: 12 }}>
              {density === "problems"
                ? "Only the pins the parcel check disputes."
                : "All of them, coloured by who put the pin there — this is how you check the work."}
            </span>
          </div>
          {/* THE SCOPE PICKER, and it only exists in "every door" mode. The queue view is a county
              queue by design; this is the audit view, and an audit of a county is not a thing
              anybody is asked to do. Turf is licensed by district and cut by precinct. */}
          {density === "every" && (
            <div className="row" style={{ gap: 6, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
              <span className="muted" style={{ fontSize: 12 }}>Show</span>
              {SCOPE_KINDS.map((k) => (
                <button key={k.v} className={"btn sm" + (scopeKind === k.v ? " accent" : "")}
                        onClick={() => setScopeKind(k.v)}>{k.label}</button>
              ))}
              {scopeKind !== "county" && (
                <>
                  <Select size="sm" value={scopeValue} onChange={setScopeValue} hint="Area"
                    options={[["", scopeKind === "precinct" && scope === "" ? "Pick a county first…"
                                : scopesQ.query.status === "loading" ? "Loading…"
                                : `Choose a ${SCOPE_KINDS.find((k) => k.v === scopeKind)!.label.toLowerCase()}…`] as [string, string],
                              ...scopes.map((sc) => [sc.value, `${sc.label}${sc.detail ? ` — ${sc.detail}` : ""}`] as [string, string])]} />
                  <span className="muted" style={{ fontSize: 12 }}>
                    Loads whole — no cap, no panning to see the rest.
                  </span>
                </>
              )}
              {scopeKind === "county" && (
                <span className="muted" style={{ fontSize: 12 }}>
                  A county is too big to draw at once, so this one follows the map — pan and zoom to load.
                </span>
              )}
            </div>
          )}
          <div className="row" style={{ flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            {(density === "problems" ? LEGEND : DENSE_LEGEND.map(([c, l]) => [DENSE_COLOUR[c], l] as [string, string]))
              .map(([c, label]) => (
              <span key={label} className="muted" style={{ fontSize: 12 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: c,
                               display: "inline-block", marginRight: 5 }} />{label}
              </span>
            ))}
            <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>
              {density === "problems"
                ? `${pts.length.toLocaleString()} shown · click one to read it, drag it to move the pin`
                : denseBusy ? "Loading…"
                : dense ? `${dense.n.toLocaleString()} doors in view · click one to read it, drag it to move the pin`
                : "Pan or zoom to load doors"}
            </span>
          </div>
          {/* A map quietly showing half a county is worse than one that says it is. */}
          {density === "problems" && queryData(mapQ.query)?.truncated && (
            <div className="err" style={{ marginTop: 8, fontSize: 12 }}>
              Showing the first {queryData(mapQ.query)?.cap.toLocaleString()} only — this county has
              more than that still open. Work a part of it, or use the queue.
            </div>
          )}
          {/* WHAT YOU ARE HOLDING, AND HOW TO TAKE IT BACK. The canvas carries no text, so this is
              the only place the address appears -- and it is the thing that makes the map usable:
              read the address here, find the roof on the satellite, drag the dot onto it. */}
          {(heldAddr || undo.length > 0) && (
            <div style={{ marginTop: 8, paddingTop: 8,
                          borderTop: "1px solid rgba(128,128,128,.35)" }}>
              <div className="row" style={{ gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
              {heldAddr && (
                <>
                  <span style={{ fontWeight: 700 }}>{heldAddr.full_addr || heldAddr.addr_key}</span>
                  {/* WHERE THE RECORD CAME FROM. This replaces the inference 0248 shipped here --
                      see RECORD_SOURCE above for why "no town or ZIP" was never the tell. */}
                  <RecordSource source={heldAddr.record_source} />
                  {/* THE TOWN AND ZIP, WHICH THE SERVER HAS BEEN SENDING SINCE 0232 -- now just a
                      fact about this row rather than a claim about where it came from. 22% of the
                      9-1-1 file has no ZIP either, so the blank means only what it says. */}
                  {heldAddr.post_comm !== undefined && (
                    <span className="muted" style={{ fontSize: 12 }}
                          title={[heldAddr.post_comm, heldAddr.post_code].filter(Boolean).length
                                 ? undefined
                                 : "Nothing to corroborate the street line with. Common: 2,684,704 "
                                   + "rows from the 9-1-1 file have no ZIP either."}>
                      {[heldAddr.post_comm, heldAddr.post_code].filter(Boolean).join(" ")
                        || "no town or ZIP"}
                    </span>
                  )}
                  <span className="muted" style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
                    {heldAddr.lat.toFixed(6)}, {heldAddr.lon.toFixed(6)}
                  </span>
                  {heldAddr.review_status && (
                    <span style={{ fontSize: 12, fontWeight: 600 }}>
                      {REVIEW_LABEL[heldAddr.review_status] || heldAddr.review_status}
                    </span>
                  )}
                  {heldAddr.placed_just_now
                    ? <span style={{ fontSize: 12, color: "var(--accent,#3fc079)", fontWeight: 600 }}>moved ✓</span>
                    : <span className="muted" style={{ fontSize: 12 }}>drag it onto the roof</span>}
                </>
              )}
              {undo.length > 0 && (
                <button className="btn sm" disabled={busy} onClick={undoLast}
                        style={{ marginLeft: "auto" }}>
                  Undo{undo.length > 1 ? ` (${undo.length})` : ""} — {undo[0].label}
                </button>
              )}
              </div>
              {/* THE THREE VERDICTS, NOW REACHABLE FROM THE MAP. They were written in 0165 and
                  rendered only on the queue card, so the screen where a wrong pin is actually
                  SEEN could not record what had been seen. Same endpoint, same reversibility. */}
              {heldAddr && (
                <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                  {DISPOSITIONS.map((d) => (
                    <button key={d.status} className="btn sm" disabled={busy} title={d.hint}
                            onClick={() => judgeHeld(d.status)}>
                      <span style={{ opacity: 0.5, marginRight: 6 }}>{d.key}</span>{d.label}
                    </button>
                  ))}
                </div>
              )}
              {heldAddr && (
                <Lookup state={lookup} busy={busy}
                        seed={{ key: heldAddr.addr_key,
                                text: [heldAddr.full_addr || heldAddr.addr_key,
                                       heldAddr.post_comm || "",
                                       heldAddr.post_code || ""].filter(Boolean).join(", ") }}
                        onRun={(q) => runLookup(heldAddr.addr_key, q,
                                                { lat: heldAddr.lat, lon: heldAddr.lon })}
                        onTake={(h) => placeHeld(h.lat, h.lon, `Google geocode: ${h.label}`)}
                        onShow={(h) => flyTo(h.lat, h.lon)} />
              )}
            </div>
          )}
          {density === "every" && dense?.truncated && (
            <div className="err" style={{ marginTop: 8, fontSize: 12 }}>
              More than 120,000 doors are in this view, so this is not all of them. Zoom in to see
              every one — the count above is what is drawn, not what is there.
            </div>
          )}
          {mapQ.query.status === "error" && (
            <div className="err" style={{ marginTop: 8 }}>
              Could not load the county map: {String(mapQ.query.error)}{" "}
              <button className="btn sm" onClick={mapQ.reload}>Retry</button>
            </div>
          )}
        </div>
      )}

      {/* ONE MAP ELEMENT, ALWAYS MOUNTED.
          The first cut rendered a map div in each view. Switching views unmounts the element the
          Google Map instance is bound to, so the map goes blank and never recovers -- and creating a
          second Map to fix that would double the Maps bill and leak the first. The card appears
          BESIDE the map instead, so the element itself is never torn down. */}
      {scope !== "" && (
        <div className="row" style={{ alignItems: "stretch", gap: 12 }}>
          {view === "queue" && cur && (
          <div className="card" style={{ flex: "0 0 340px", padding: 14 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{cur.full_addr || cur.addr_key}</div>
            <div className="row" style={{ marginBottom: 10, gap: 8 }}>
              <span className="muted">{cur.county}</span>
              {/* THE ANSWER, ON THE CARD THAT ASKED. Staying on a judged address is only an
                  improvement if the card says what it became; otherwise it is the same three
                  buttons and no way to tell the verdict landed. */}
              {cur.review_status && (
                <span style={{ fontSize: 12, fontWeight: 600 }}>
                  {REVIEW_LABEL[cur.review_status] || cur.review_status}
                </span>
              )}
              {cur.placed_just_now && (
                <span style={{ fontSize: 12, color: "var(--accent,#3fc079)", fontWeight: 600 }}>
                  pin placed ✓
                </span>
              )}
              {cur.lat != null && (
                <a className="muted" style={{ fontSize: 11 }} target="_blank" rel="noreferrer"
                   href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cur.full_addr || "")}`}>
                  Open in Google Maps ↗
                </a>
              )}
            </div>

            {cur.conflict_reason && (
              <div style={{ background: "#3a2f1f", border: "1px solid #7a5c20", borderRadius: 6, padding: 10, marginBottom: 10 }}>
                <strong style={{ fontSize: 13 }}>The automatic pass declined this one</strong>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {CONFLICT_TEXT[cur.conflict_reason] || cur.conflict_reason}
                </div>
                {cur.conflict_detail && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{cur.conflict_detail}</div>}
                {(cur.conflict_candidates?.length ?? 0) > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
                      Marked on the map. Click one to take it, or drag the red pin somewhere else —
                      the right answer is often neither.
                    </div>
                    {cur.conflict_candidates!.map((c, n) => {
                      // FIVE ROWS SAYING "A parcel claiming this address" TELL YOU NOTHING. What
                      // separates them is WHERE they are, so lead with that: distance and bearing
                      // from the current pin, which is the thing you are comparing against on the map.
                      const d = cur.lat != null && cur.lon != null
                        ? metresBetween(cur.lat, cur.lon, c.lat, c.lon) : null;
                      const armed = dragged && Math.abs(dragged.lat - c.lat) < 1e-9
                                             && Math.abs(dragged.lon - c.lon) < 1e-9;
                      return (
                        <div key={n} className="row" style={{ gap: 6, alignItems: "center", padding: "3px 0" }}>
                          <span style={{ width: 18, height: 18, borderRadius: "50%",
                                         background: armed ? "#3ddc84" : "#ffb547",
                                         color: "#1a1d23", font: "700 11px system-ui", display: "flex",
                                         alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
                            {String.fromCharCode(65 + n)}
                          </span>
                          <span style={{ fontSize: 12 }}>
                            {d != null ? `${Math.round(d)} m ${bearing(cur.lat!, cur.lon!, c.lat, c.lon)}` : "—"}
                            <span className="muted" style={{ marginLeft: 6 }}>
                              {c.kind === "address" ? (c.label || "another address") : "parcel"}
                            </span>
                          </span>
                          <a className="muted" style={{ fontSize: 11, marginLeft: "auto" }}
                             href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${c.lat},${c.lon}`}
                             target="_blank" rel="noreferrer">Street View ↗</a>
                          {/* An explicit control. Clicking the map pin also arms it, but a pin that
                              silently arms with no visible commit is not a way to choose anything. */}
                          <button className={"btn sm" + (armed ? " accent" : "")} disabled={busy}
                                  onClick={() => setDragged({ lat: c.lat, lon: c.lon })}>
                            {armed ? "chosen" : "use"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Server-side evidence for the judgement a reviewer would otherwise make by eye. */}
            <dl style={{ margin: 0, fontSize: 13 }}>
              <Row k="Current grade" v={cur.geocode_quality} />
              <Row k="County parcel data" v={cur.county_pct_own != null ? `${cur.credibility} (${cur.county_pct_own}% own-parcel)` : cur.credibility} />
              {cur.parcel_addr_count != null && (
                <Row k="Addresses on this lot"
                     v={cur.parcel_addr_count > 20
                        ? `${cur.parcel_addr_count} — apartments or a mobile-home park`
                        : String(cur.parcel_addr_count)} />
              )}
              {cur.parcel_area_m2 ? <Row k="Lot size" v={`${Math.round(cur.parcel_area_m2).toLocaleString()} m²`} /> : null}
            </dl>

            <div style={{ marginTop: 14, display: "grid", gap: 6 }}>
              {DISPOSITIONS.map((d) => (
                <button key={d.status} className="btn" disabled={busy} onClick={() => judge(d.status)} title={d.hint}>
                  <span style={{ opacity: 0.5, marginRight: 8 }}>{d.key}</span>{d.label}
                </button>
              ))}
              <button className="btn accent" disabled={busy || !dragged} onClick={() => placeHere()}
                      title="Drag the pin on the map, then commit it here">
                {dragged ? `Place pin${chosenLetter ? ` at ${chosenLetter}` : " here"}  ⏎`
                         : "Drag the pin, or choose one above"}
              </button>
              <button className="btn sm" disabled={busy} onClick={advance}>Skip  →</button>
              {/* Reopening a judged address needs a way to UNDO the judgement, not just re-judge it. */}
              <button className="btn sm" disabled={busy} onClick={() => judge("clear")}
                      title="Remove the verdict and put this address back in the queue">
                Put back in the queue
              </button>
            </div>
            <div className="muted" style={{ fontSize: 11, marginTop: 10 }}>
              A pin you place by hand is permanent — no automatic pass can move it.
            </div>
            {/* THE SAME PANEL THE MAP CARRIES. One implementation, because "where is this actually"
                is the same question from both screens, and a second copy is how the two drift. */}
            <Lookup state={lookup} busy={busy}
                    seed={{ key: cur.addr_key, text: cur.full_addr || cur.addr_key }}
                    onRun={(q) => runLookup(cur.addr_key, q,
                                            cur.lat != null && cur.lon != null
                                              ? { lat: cur.lat, lon: cur.lon } : null)}
                    onTake={(h) => placeHere({ lat: h.lat, lon: h.lon },
                                             `Google geocode: ${h.label}`)}
                    onShow={(h) => flyTo(h.lat, h.lon)} />
          </div>)}
          <div className="card" style={{ flex: 1, padding: 0, minHeight: 620, overflow: "hidden" }}>
            <div ref={setMapEl} style={{ width: "100%", height: "100%", minHeight: 620 }} />
          </div>
        </div>
      )}
    </div>
  );
}

function metresBetween(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371008.8, r = Math.PI / 180;
  const dLat = (lat2 - lat1) * r, dLon = (lon2 - lon1) * r;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// "29 m NE" is a thing you can find on the map. "A parcel claiming this address" is not.
function bearing(lat1: number, lon1: number, lat2: number, lon2: number): string {
  const r = Math.PI / 180;
  const y = Math.sin((lon2 - lon1) * r) * Math.cos(lat2 * r);
  const x = Math.cos(lat1 * r) * Math.sin(lat2 * r)
          - Math.sin(lat1 * r) * Math.cos(lat2 * r) * Math.cos((lon2 - lon1) * r);
  const deg = (Math.atan2(y, x) / r + 360) % 360;
  return ["N", "NE", "E", "SE", "S", "SW", "W", "NW"][Math.round(deg / 45) % 8];
}

// ── THE SEARCH RESULTS ───────────────────────────────────────────────────────────────────────
//
// TWO LISTS, BOTH SCROLLING, NEITHER TRUNCATED. Caleb, deciding the shape: "cap the space for the
// list to appear and then allow the list to be scrolled ... same real estate for the screen, but
// allows all the results to populate". He is right that no count is needed: the ceiling is one row
// per county, and the most repeated address text in Texas is '100 MAIN ST' at 52 rows across 47
// counties. A fixed-height box holds that, so nothing is ever hidden behind a "+ 12 more".
//
// EXPORTED SO A TEST CAN REACH IT, like Lookup and parcelDataLabel. The rule that a row from
// another county must show its county, its town and its ZIP lives in this markup and nowhere else.
// ONE CHIP, TWO PLACES. The strip under the map and the out-of-county list are both asking the
// same question of a row, and a second copy of this is how the two drift into saying different
// things about the same fact.
// ── THE GUIDE, ON THE SCREEN IT DESCRIBES ────────────────────────────────────────────────────
//
// NOT A HELP PAGE. Caleb approved "a ? icon that opens a small panel", and the reason it is not a
// separate page is in the backlog item: a guide nobody navigates to is the same as no guide, which
// is the state being fixed. It sits in the control row, so it is on screen from both the queue and
// the map -- the keys work in both.
export function ShortcutHelp() {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative" }}>
      <button className="btn sm" type="button" aria-expanded={open}
              aria-label="Keyboard shortcuts"
              title="Keyboard shortcuts"
              onClick={() => setOpen((v) => !v)}>?</button>
      {open && (
        <div className="card" role="dialog" aria-label="Keyboard shortcuts"
             style={{ position: "absolute", zIndex: 30, top: "calc(100% + 6px)", right: 0,
                      width: 340, padding: 12, textAlign: "left",
                      boxShadow: "0 6px 24px rgba(0,0,0,.35)" }}>
          <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
            <strong style={{ fontSize: 13 }}>Keyboard shortcuts</strong>
            <button className="btn sm" type="button" onClick={() => setOpen(false)}
                    aria-label="Close">×</button>
          </div>
          <ShortcutTable />
        </div>
      )}
    </span>
  );
}

// SPLIT OUT SO THE TEST CAN RENDER WHAT YOU ACTUALLY SEE. ShortcutHelp keeps the panel behind
// `open`, and renderToStaticMarkup cannot click -- asserting against a copy of the wording retyped
// in the test would only prove the test agrees with itself.
export function ShortcutTable() {
  return (
    <>
          <div style={{ display: "grid", gap: 8 }}>
            {SHORTCUTS.map((k) => (
              // ALIGN TO THE TOP, AND SIZE THE CHIP TO ITS TEXT. A grid item stretches to the row
              // height by default, so the three-line rows drew a tall empty box beside the key --
              // and 46px clipped "Enter". Seen on production once the panel was actually opened;
              // the render test could not have caught it, because it asserts text, not boxes.
              <div key={k.key} style={{ display: "grid", gridTemplateColumns: "54px 1fr", gap: 8,
                                        alignItems: "start" }}>
                <kbd style={{ fontSize: 12, fontWeight: 700, textAlign: "center",
                              padding: "3px 0", lineHeight: 1.2, alignSelf: "start",
                              border: "1px solid rgba(128,128,128,.5)", borderRadius: 4,
                              fontVariantNumeric: "tabular-nums" }}>{k.shows}</kbd>
                <span>
                  <span style={{ fontSize: 13 }}>{k.label}</span>
                  <span className="muted" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
                    {k.hint}
                  </span>
                </span>
              </div>
            ))}
          </div>
          {/* THE GUARD IS PART OF THE RULE, so it is part of the guide. The handler ignores every
              key while the focus is in a text box or a select, which is why 1 does not judge the
              card while you are typing an address into the search. Leaving that out would make the
              panel wrong in the one moment somebody checks it. */}
          <div className="muted" style={{ fontSize: 11, marginTop: 10, paddingTop: 8,
                                          borderTop: "1px solid rgba(128,128,128,.35)" }}>
            The keys act on whatever is in front of you — the card on the queue, the pin you clicked
            on the map. They do nothing while you are typing in a box, so finish in the search
            field first.
          </div>
    </>
  );
}

export function RecordSource({ source }: { source?: string | null }) {
  const s = source ? RECORD_SOURCE[source] : undefined;
  if (!s) return null;
  return (
    <span className="chip" style={{ fontSize: 11 }} title={s.hint}>{s.label}</span>
  );
}

export function SearchHits({ hits, fips, countyName, onPick }: {
  hits: MapPt[] | null;
  fips: number | "";
  countyName: string | null;
  onPick: (p: MapPt) => void;
}) {
  if (!hits || hits.length === 0) return null;
  const scoped = fips !== "";
  // SCOPE IS RECOMPUTED FROM THE COUNTY ON SCREEN, not read off the server's flag.
  //
  // The flag was computed when the SEARCH ran, and clicking a result now CHANGES the county -- so
  // the moment you click the Hays row, every row in this list is still carrying `in_scope: false`
  // from a search made with no county chosen. The screen then said "Nothing in HAYS" directly above
  // an open card for a Hays address. Seen on production immediately after 0248 deployed.
  //
  // The server's flag stays as the fallback, and it has to: rows from the map and the reviewed list
  // carry no fips at all, having been asked for one county, so absent means local.
  const isHere = (h: MapPt) =>
    h.fips != null ? (scoped && h.fips === fips) : h.in_scope !== false;
  const here = hits.filter(isHere);
  const away = hits.filter((h) => !isHere(h));
  const row = (h: MapPt, withCounty: boolean) => (
    <button key={`${h.fips ?? "local"}:${h.addr_key}`} className="btn sm"
            style={{ justifyContent: "flex-start" }} onClick={() => onPick(h)}>
      <span style={{ width: 9, height: 9, borderRadius: "50%", background: ptColour(h),
                     display: "inline-block", marginRight: 8, flex: "0 0 auto" }} />
      {withCounty && <span style={{ fontWeight: 700, marginRight: 8 }}>{h.county || h.fips}</span>}
      {h.full_addr || h.addr_key}
      {/* THE TOWN AND ZIP EARN THEIR PLACE ONLY ON THE AWAY LIST, because that is where you are
          deciding whether a row is the same house or a different one. They are evidence FOR a row,
          never against one: a blank says the record has none, not where it came from -- which is
          what the chip beside it is for. */}
      {withCounty && (
        <>
          <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>
            {[h.post_comm, h.post_code].filter(Boolean).join(" ") || "no town or ZIP"}
          </span>
          <span style={{ marginLeft: 8 }}><RecordSource source={h.record_source} /></span>
        </>
      )}
      <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>
        {h.review_status ? "judged" : h.placed_by_human ? "your pin"
          : h.conflict_reason ? "needs you" : h.geocode_quality}
      </span>
    </button>
  );
  return (
    <div className="card" style={{ padding: 10, marginBottom: 12 }}>
      {here.length > 0 && (
        <>
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            {here.length} match{here.length === 1 ? "" : "es"}{countyName ? ` in ${countyName}` : ""}
            {" "}— including addresses nothing is complaining about, so you can spot-check ones the
            system thinks are fine.
          </div>
          <div style={{ display: "grid", gap: 4, maxHeight: 190, overflowY: "auto" }}>
            {here.map((h) => row(h, false))}
          </div>
        </>
      )}
      {/* THE SENTENCE THAT WAS THE WHOLE BUG. "Not in the vault" was the entire answer when the
          address was sitting in the county next door. */}
      {here.length === 0 && away.length > 0 && scoped && (
        <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
          Nothing in {countyName || "this county"} — but this address exists elsewhere in Texas.
        </div>
      )}
      {away.length > 0 && (
        <>
          <div className="muted"
               style={{ fontSize: 12,
                        margin: here.length > 0 ? "12px 0 6px" : "0 0 6px",
                        paddingTop: here.length > 0 ? 10 : 0,
                        borderTop: here.length > 0 ? "1px solid rgba(128,128,128,.35)" : undefined }}>
            {away.length} elsewhere in Texas — the same address text filed in another county. Often
            that is just another town's Main Street. Sometimes it is where the address really is,
            and the row you were looking at should not exist.
          </div>
          <div style={{ display: "grid", gap: 4, maxHeight: 190, overflowY: "auto" }}>
            {away.map((h) => row(h, true))}
          </div>
        </>
      )}
    </div>
  );
}

// ── THE LOOKUP PANEL ─────────────────────────────────────────────────────────────────────────
//
// THE QUERY IS EDITABLE, AND THAT IS NOT A CONVENIENCE. It is seeded from the record, and the
// record is frequently the thing that is wrong: "3700 MT GAINOR RD" with no town is ambiguous to
// Google in exactly the way it is ambiguous to a person, and typing the town is the only way
// through. A fixed query would make the one case this exists for unanswerable.
//
// EVERY RESULT IS LISTED. Caleb: "if google isnt sure and multiple addresses can populate under the
// results that we give it... our system is showing all those options rather than just giving me one
// of them". 146 HONEYSUCKLE returns Zavalla AND Burke; picking one for him would be the same guess
// the parcel pass was retired for making.
export function Lookup({ state, seed, busy, onRun, onTake, onShow }: {
  state: { key: string; q: string; hits: GeoHit[] | null; err: string | null; running: boolean } | null;
  seed: { key: string; text: string };
  busy: boolean;
  onRun: (q: string) => void;
  onTake: (h: GeoHit) => void;
  onShow: (h: GeoHit) => void;
}) {
  const mine = state && state.key === seed.key ? state : null;
  const [q, setQ] = useState(seed.text);
  // Re-seed on the ADDRESS, not on the text. Two doors can carry identical text -- that is how the
  // Travis phantom and the real Hays house both read "3700 MT GAINOR RD" -- and keying on the text
  // would silently keep one door's edits on the next one.
  useEffect(() => { setQ(seed.text); }, [seed.key]);   // eslint-disable-line react-hooks/exhaustive-deps
  const anyHouse = (mine?.hits ?? []).some((h) => geoIsPlaceable(h.precision));
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(128,128,128,.35)" }}>
      <div className="row" style={{ gap: 6 }}>
        <input className="ctl" style={{ flex: 1, minWidth: 0 }} value={q}
               aria-label="Address to look up"
               placeholder="Add a town if Google guesses wrong"
               onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onRun(q); } }} />
        <button className="btn sm" disabled={busy || !!mine?.running} onClick={() => onRun(q)}>
          {mine?.running ? "Looking…" : "Look it up"}
        </button>
      </div>

      {mine?.err && (
        <div className="err" style={{ fontSize: 12, marginTop: 6 }}>
          Google could not answer: {mine.err}
        </div>
      )}

      {mine?.hits != null && mine.hits.length === 0 && (
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          Google has nothing for that text anywhere in Texas.
        </div>
      )}

      {!!mine?.hits?.length && (
        <div style={{ marginTop: 6 }}>
          {mine.hits.map((h, n) => {
            const house = geoIsPlaceable(h.precision);
            return (
              <div key={n} className="row"
                   style={{ gap: 6, alignItems: "flex-start", padding: "4px 0",
                            opacity: house ? 1 : 0.65 }}>
                <span style={{ width: 18, height: 18, borderRadius: "50%", flex: "0 0 auto",
                               marginTop: 2, background: house ? "#8bd3ff" : "#79818d",
                               color: "#101318", font: "700 11px system-ui", display: "flex",
                               alignItems: "center", justifyContent: "center" }}>
                  {String.fromCharCode(65 + n)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12 }}>{h.label}</div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {GEO_PRECISION[h.precision] || h.precision}
                    {h.partial && " · Google altered the text to reach this"}
                    {geoDistance(h.m, h.dir) && ` · ${geoDistance(h.m, h.dir)}`}
                  </div>
                </div>
                {/* SHOW IS NOT TAKE. An answer a hundred miles off screen has to be lookable-at
                    before it is commitable -- that is the whole reason this screen has a map. */}
                <button className="btn sm" onClick={() => onShow(h)}
                        title="Move the map there. The pin does not move.">Show</button>
                {house && (
                  <button className="btn sm accent" disabled={busy} onClick={() => onTake(h)}
                          title="Put this address's pin here. Permanent — no automated pass can move it.">
                    Move the pin
                  </button>
                )}
              </div>
            );
          })}
          {!anyHouse && (
            <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
              Not one of these is a house — Google found the road or the area and stopped. That is
              what an address that does not exist looks like from here.
            </div>
          )}
          <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
            Every answer Google returned, in Google's own order. Only the ones it places at a house
            can move a pin.
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string | null }) {
  if (!v) return null;
  return (
    <div className="row" style={{ justifyContent: "space-between", padding: "3px 0" }}>
      <dt className="muted" style={{ margin: 0 }}>{k}</dt>
      <dd style={{ margin: 0, textAlign: "right" }}>{v}</dd>
    </div>
  );
}
